import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft,
  Wifi,
  WifiOff,
  Plus,
  DoorOpen,
  RefreshCw,
  Loader2,
  Search,
  Swords,
  X,
  AlertTriangle,
  SlidersHorizontal,
  ChevronDown,
  Lock,
  Check,
  UserMinus,
  UserPlus,
  Star,
  MessagesSquare,
  Mail,
} from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import { belotMatchTargetCpFromEngine } from '../game/belotArmenianCards';
import { ArcadeModeBackdrop } from '../poker-ui/ArcadeModeBackdrop';
import { NotificationToast } from './GameEffects';
import { createOnlineLobbyTransport } from '../realtime/onlineLobbyTransport';
import {
  duplicateSessionUserMessage,
  finalDisconnectUserMessage,
  isReconnectFallbackJoin,
  reconnectSuccessUserMessage,
} from '../realtime/connectionUx';
import {
  normalizeBuyInValue,
  ROOM_THEME,
  SEKA_ROOM_ANTE_OPTIONS,
  BUCKS_IMG,
} from './LobbyTablesBlock';
import { OnlineStyleCreateRoomModal } from './OnlineStyleCreateRoomModal';
import { LobbyBottomNav, getPlayerLeague, LobbyFiskiIcon, LobbyHubNotificationIndicator } from './lobbyHubShared';
import './Lobby.css';

const ROOM_STAKE_OPTIONS = [100, 200, 500, 1000, 2500, 5000, 10000, 50000, 100000];

function OnlineLobbyPlayerRankBadge({ level }) {
  const lvl = Math.max(1, Math.floor(Number(level) || 1));
  const league = getPlayerLeague(lvl);
  return (
    <span
      className="relative inline-flex h-6 w-6 shrink-0 items-center justify-center"
      aria-label={`Уровень ${lvl}`}
    >
      <Star className={`absolute inset-0 h-6 w-6 drop-shadow-md ${league.starClass}`} strokeWidth={1.15} aria-hidden />
      <span
        className="relative z-[1] font-black tabular-nums text-[9px] text-white"
        style={{ WebkitTextStroke: '1px rgb(0 0 0)', paintOrder: 'stroke fill' }}
      >
        {lvl}
      </span>
    </span>
  );
}

function toDisplayNicknameLobby(rawValue) {
  const raw = String(rawValue || '').trim();
  if (!raw) return 'Игрок';
  if (/^(u|dev)_[a-z0-9_-]{8,}$/i.test(raw)) return 'Игрок';
  return raw;
}

/** Микро-аватар + звезда/уровень — ~⅓ от карточки LobbyTablesBlock. */
function OnlineLobbyCompactSeatChip({ userId, room, user }) {
  const id = String(userId);
  const prof = (room.playerProfiles && room.playerProfiles[id]) || {};
  const isSelf = user?.id && String(user.id) === id;
  const un = toDisplayNicknameLobby(isSelf ? user?.username : prof.username || id);
  const avatar = String((isSelf && user?.avatarUrl) || prof.avatarUrl || '').trim();
  const lvl = Math.max(
    1,
    Math.floor(
      isSelf ? Number(user?.level) || Number(prof.level) || 1 : Number(prof.level) || 1
    )
  );
  const league = getPlayerLeague(lvl);
  const initial = (un || 'И').charAt(0).toUpperCase();
  return (
    <div className="flex shrink-0 items-center gap-px" title={un}>
      <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/30 bg-black/50 text-[7px] font-bold text-white">
        {avatar ? (
          <img src={avatar} alt="" className="h-full w-full object-cover" draggable={false} />
        ) : (
          initial
        )}
      </div>
      <span className="relative inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center" aria-label={`Уровень ${lvl}`}>
        <Star className={`absolute inset-0 h-[14px] w-[14px] ${league.starClass}`} strokeWidth={1.1} aria-hidden />
        <span
          className="relative z-[1] font-black tabular-nums text-[5px] leading-none text-white"
          style={{ WebkitTextStroke: '0.4px rgb(0 0 0)', paintOrder: 'stroke fill' }}
        >
          {lvl}
        </span>
      </span>
    </div>
  );
}
const ONLINE_THEME = {
  seka: {
    modeLabel: 'Сека',
    mode: 'seka',
    logoSrc: '/logotip-seka-button.png',
    iconTone: 'text-amber-200',
    shellCard: 'border-amber-300/25 bg-gradient-to-br from-[#4a280f]/88 via-[#3a1f0c]/84 to-[#2b1608]/88',
    panelCard: 'border-amber-300/20 bg-gradient-to-b from-[#111a2a]/92 to-[#0b1320]/92',
    chip: 'border-amber-300/35 bg-amber-500/12 text-amber-100',
    accentText: 'text-amber-200',
    actionBtn: 'border-amber-300/45 bg-amber-500/15 text-amber-100 hover:border-amber-200/65 hover:bg-amber-500/24',
    joinBtn: 'border-amber-300/45 bg-amber-500/15 text-amber-100 hover:bg-amber-500/26',
    sliderTrack: 'rgba(251,191,36,0.68)',
  },
  poker: {
    modeLabel: 'Покер',
    mode: 'poker',
    logoSrc: '/logotip-poker-button.png',
    iconTone: 'text-emerald-200',
    shellCard: 'border-emerald-300/25 bg-gradient-to-br from-[#123322]/88 via-[#102a1c]/84 to-[#0b1b13]/88',
    panelCard: 'border-emerald-300/20 bg-gradient-to-b from-[#111a2a]/92 to-[#0b1320]/92',
    chip: 'border-emerald-300/35 bg-emerald-500/12 text-emerald-100',
    accentText: 'text-emerald-200',
    actionBtn: 'border-emerald-300/45 bg-emerald-500/14 text-emerald-100 hover:border-emerald-200/65 hover:bg-emerald-500/24',
    joinBtn: 'border-emerald-300/45 bg-emerald-500/14 text-emerald-100 hover:bg-emerald-500/24',
    sliderTrack: 'rgba(52,211,153,0.68)',
  },
  belot: {
    modeLabel: 'Белот',
    mode: 'belot',
    logoSrc: '/belot-button.png',
    iconTone: 'text-violet-200',
    shellCard: 'border-violet-300/25 bg-gradient-to-br from-[#2f164d]/88 via-[#25133f]/84 to-[#1a0f30]/88',
    panelCard: 'border-violet-300/20 bg-gradient-to-b from-[#111a2a]/92 to-[#0b1320]/92',
    chip: 'border-violet-300/35 bg-violet-500/12 text-violet-100',
    accentText: 'text-violet-200',
    actionBtn: 'border-violet-300/45 bg-violet-500/14 text-violet-100 hover:border-violet-200/65 hover:bg-violet-500/24',
    joinBtn: 'border-violet-300/45 bg-violet-500/14 text-violet-100 hover:bg-violet-500/24',
    sliderTrack: 'rgba(167,139,250,0.68)',
  },
};

/** Экран ожидания стола: общая форма, палитра зависит от режима (янтарь / изумруд / сирень). */
const PUBG_WAITING_BY_MODE = {
  seka: {
    page: 'bg-[#0b0b0b]',
    vignette:
      'pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(ellipse_120%_70%_at_50%_-15%,rgba(234,88,12,0.2),transparent_55%),radial-gradient(ellipse_at_100%_110%,rgba(154,52,18,0.12),transparent_40%)]',
    header:
      'relative z-20 border-b-2 border-amber-700/55 bg-gradient-to-b from-[#141210] via-[#0f0e0d] to-[#0a0908] px-3 py-2.5 shadow-[0_6px_28px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(251,191,36,0.12)] sm:px-4',
    backBtn:
      'lobby-focus inline-flex h-9 w-9 items-center justify-center rounded-sm border-2 border-amber-800/60 bg-[#1a1816] text-amber-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition hover:border-amber-500 hover:bg-[#242220]',
    titleKicker: 'text-[10px] font-black uppercase tracking-[0.22em] text-amber-500',
    titleMain:
      'text-sm font-black uppercase tracking-[0.08em] text-amber-100 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] sm:text-base',
    statusChip:
      'inline-flex items-center gap-1 rounded-sm border-2 border-amber-700/55 bg-black/60 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-amber-400',
    panel:
      'relative z-10 overflow-hidden rounded-sm border-2 border-amber-700/45 bg-gradient-to-b from-[#1c1a18] via-[#141210] to-[#0e0d0c] p-4 shadow-[inset_0_1px_0_rgba(251,191,36,0.1),0_16px_48px_rgba(0,0,0,0.55)]',
    panelTopSheen:
      'pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent',
    introTitle: 'text-[10px] font-black uppercase tracking-[0.2em] text-amber-500',
    logo: 'h-11 w-auto shrink-0 object-contain opacity-95 drop-shadow-[0_0_10px_rgba(251,191,36,0.35)]',
    divider: 'my-4 h-px bg-gradient-to-r from-transparent via-amber-800/45 to-transparent',
    codeBox:
      'rounded-sm border-2 border-amber-800/50 bg-black/60 px-4 py-4 text-center shadow-[inset_0_0_28px_rgba(245,158,11,0.07),0_0_24px_rgba(234,88,12,0.1)]',
    codeLabel: 'text-[10px] font-black uppercase tracking-[0.28em] text-amber-600',
    codeValue:
      'mt-1.5 font-mono text-2xl font-black tracking-[0.24em] text-amber-300 sm:text-3xl [text-shadow:0_0_18px_rgba(251,191,36,0.35)]',
    statRow: 'text-[11px] font-bold uppercase tracking-wider text-stone-500',
    statAccent: 'font-black text-amber-400 tabular-nums',
    playerRow:
      'flex items-center gap-3 border border-amber-950/80 bg-gradient-to-r from-[#1a1815] to-[#12100e] py-2.5 pl-2 pr-3 shadow-[inset_4px_0_0_0_rgba(245,158,11,0.9)]',
    avatarWrap:
      'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-sm border-2 border-amber-700/55 bg-[#0c0b0a] text-sm font-black text-amber-200 shadow-[inset_0_0_14px_rgba(0,0,0,0.85)]',
    name: 'min-w-0 truncate text-sm font-bold uppercase tracking-wide text-stone-100',
    hostBadge:
      'rounded-sm border border-amber-700/50 bg-amber-950/70 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-amber-300',
    readyBadge:
      'inline-flex items-center gap-0.5 rounded-sm border border-emerald-600/45 bg-emerald-950/50 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-emerald-400',
    notReady: 'text-[10px] font-semibold uppercase tracking-wide text-stone-600',
    kickBtn:
      'lobby-focus inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border-2 border-red-700/55 bg-red-950/50 text-red-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:bg-red-950/80 disabled:opacity-40',
    readyBtnOn:
      'lobby-focus flex w-full min-h-[3.25rem] items-center justify-center rounded-sm border-2 border-amber-700 bg-gradient-to-b from-stone-700 via-stone-800 to-stone-950 px-4 py-4 text-base font-black uppercase tracking-[0.12em] text-amber-200 shadow-[0_4px_0_rgb(28,25,23),0_10px_28px_rgba(0,0,0,0.45)] transition hover:brightness-110 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(28,25,23)] disabled:pointer-events-none disabled:opacity-40 sm:min-h-[3.5rem] sm:text-lg',
    readyBtnOff:
      'lobby-focus flex w-full min-h-[3.25rem] items-center justify-center rounded-sm border-2 border-amber-300/90 bg-gradient-to-b from-amber-300 via-amber-500 to-orange-800 px-4 py-4 text-base font-black uppercase tracking-[0.15em] text-stone-950 shadow-[0_4px_0_rgb(124,45,18),0_14px_36px_rgba(234,88,12,0.42)] transition hover:brightness-105 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(124,45,18)] disabled:pointer-events-none disabled:opacity-40 sm:min-h-[3.5rem] sm:text-lg',
    footIdle:
      'flex items-center justify-center gap-2 rounded-sm border border-amber-900/40 bg-black/40 px-3 py-2.5 text-center text-[11px] font-bold uppercase tracking-wide text-stone-500',
    footActive:
      'flex items-center justify-center gap-2 rounded-sm border-2 border-amber-600/50 bg-amber-950/35 px-3 py-2.5 text-center text-[11px] font-black uppercase tracking-wide text-amber-200',
    loadingBox:
      'flex items-center justify-center gap-2 rounded-sm border border-amber-900/35 bg-black/35 px-3 py-3 text-center text-xs font-semibold text-stone-500',
    inviteOutlineBtn:
      'lobby-focus mt-3 flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-sm border-2 border-amber-700/55 bg-[#1a1816] px-3 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-amber-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:border-amber-500 hover:bg-[#242220] sm:text-sm',
    spinnerAccent: 'text-amber-500',
    accentIcon: 'text-amber-300',
    spinnerMuted: 'text-amber-600',
    loadingEmptyText: 'text-amber-900/80',
    friendAddBtn:
      'lobby-focus inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border-2 border-amber-700/55 bg-[#1a1816] text-amber-200 transition hover:border-amber-500 hover:bg-[#242220] disabled:opacity-40',
    spinnerFootActive: 'text-amber-400',
    spinnerFootIdle: 'text-amber-700',
    chatScrollBorder: 'border-amber-900/45',
    chatBubbleSelf: 'ml-4 border-amber-600/40 bg-amber-950/35 text-amber-50',
    chatNameAccent: 'font-black uppercase tracking-wide text-amber-500/90',
    chatInput:
      'min-w-0 flex-1 rounded-sm border-2 border-amber-900/50 bg-black/50 px-2 py-2 text-xs text-stone-100 placeholder:text-stone-600 focus:border-amber-600/60 focus:outline-none disabled:opacity-50',
    chatSendBtn:
      'lobby-focus shrink-0 rounded-sm border-2 border-amber-600/55 bg-amber-950/45 px-3 py-2 text-[10px] font-black uppercase tracking-wide text-amber-100 transition hover:bg-amber-900/55 disabled:opacity-40',
    modalDivider: 'border-amber-900/40',
    monoAccent: 'text-amber-500/90',
    secondaryActionBtn:
      'lobby-focus shrink-0 rounded-sm border-2 border-amber-600/55 bg-amber-950/40 px-2 py-1.5 text-[9px] font-black uppercase tracking-wide text-amber-100 transition hover:bg-amber-900/50',
    emptyPanel: 'mt-4 rounded-sm border border-amber-900/35 bg-black/35 px-3 py-4 text-center text-xs text-stone-500',
  },
  poker: {
    page: 'bg-[#070d0b]',
    vignette:
      'pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(ellipse_120%_70%_at_50%_-15%,rgba(16,185,129,0.18),transparent_55%),radial-gradient(ellipse_at_100%_110%,rgba(6,78,59,0.2),transparent_42%)]',
    header:
      'relative z-20 border-b-2 border-emerald-800/50 bg-gradient-to-b from-[#0f1814] via-[#0a1210] to-[#060a09] px-3 py-2.5 shadow-[0_6px_28px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(52,211,153,0.14)] sm:px-4',
    backBtn:
      'lobby-focus inline-flex h-9 w-9 items-center justify-center rounded-sm border-2 border-emerald-800/55 bg-[#101916] text-emerald-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:border-emerald-500 hover:bg-[#152520]',
    titleKicker: 'text-[10px] font-black uppercase tracking-[0.22em] text-emerald-500',
    titleMain:
      'text-sm font-black uppercase tracking-[0.08em] text-emerald-50 drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] sm:text-base',
    statusChip:
      'inline-flex items-center gap-1 rounded-sm border-2 border-emerald-700/50 bg-black/55 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-emerald-300',
    panel:
      'relative z-10 overflow-hidden rounded-sm border-2 border-emerald-800/45 bg-gradient-to-b from-[#121a17] via-[#0d1512] to-[#080f0d] p-4 shadow-[inset_0_1px_0_rgba(52,211,153,0.1),0_16px_48px_rgba(0,0,0,0.55)]',
    panelTopSheen:
      'pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/45 to-transparent',
    introTitle: 'text-[10px] font-black uppercase tracking-[0.2em] text-emerald-500',
    logo: 'h-11 w-auto shrink-0 object-contain opacity-95 drop-shadow-[0_0_12px_rgba(52,211,153,0.35)]',
    divider: 'my-4 h-px bg-gradient-to-r from-transparent via-emerald-800/40 to-transparent',
    codeBox:
      'rounded-sm border-2 border-emerald-800/50 bg-black/55 px-4 py-4 text-center shadow-[inset_0_0_28px_rgba(16,185,129,0.08),0_0_24px_rgba(5,150,105,0.12)]',
    codeLabel: 'text-[10px] font-black uppercase tracking-[0.28em] text-emerald-600',
    codeValue:
      'mt-1.5 font-mono text-2xl font-black tracking-[0.24em] text-emerald-300 sm:text-3xl [text-shadow:0_0_18px_rgba(52,211,153,0.35)]',
    statRow: 'text-[11px] font-bold uppercase tracking-wider text-stone-500',
    statAccent: 'font-black text-emerald-400 tabular-nums',
    playerRow:
      'flex items-center gap-3 border border-emerald-950/70 bg-gradient-to-r from-[#121916] to-[#0c1210] py-2.5 pl-2 pr-3 shadow-[inset_4px_0_0_0_rgba(16,185,129,0.85)]',
    avatarWrap:
      'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-sm border-2 border-emerald-700/50 bg-[#080d0b] text-sm font-black text-emerald-200 shadow-[inset_0_0_14px_rgba(0,0,0,0.85)]',
    name: 'min-w-0 truncate text-sm font-bold uppercase tracking-wide text-stone-100',
    hostBadge:
      'rounded-sm border border-emerald-700/45 bg-emerald-950/65 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-emerald-200',
    readyBadge:
      'inline-flex items-center gap-0.5 rounded-sm border border-emerald-500/50 bg-emerald-950/55 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-emerald-300',
    notReady: 'text-[10px] font-semibold uppercase tracking-wide text-stone-600',
    kickBtn:
      'lobby-focus inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border-2 border-red-700/55 bg-red-950/50 text-red-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:bg-red-950/80 disabled:opacity-40',
    readyBtnOn:
      'lobby-focus flex w-full min-h-[3.25rem] items-center justify-center rounded-sm border-2 border-emerald-800 bg-gradient-to-b from-stone-700 via-stone-800 to-stone-950 px-4 py-4 text-base font-black uppercase tracking-[0.12em] text-emerald-200 shadow-[0_4px_0_rgb(20,35,28),0_10px_28px_rgba(0,0,0,0.45)] transition hover:brightness-110 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(20,35,28)] disabled:pointer-events-none disabled:opacity-40 sm:min-h-[3.5rem] sm:text-lg',
    readyBtnOff:
      'lobby-focus flex w-full min-h-[3.25rem] items-center justify-center rounded-sm border-2 border-emerald-300/90 bg-gradient-to-b from-emerald-300 via-emerald-500 to-emerald-900 px-4 py-4 text-base font-black uppercase tracking-[0.15em] text-stone-950 shadow-[0_4px_0_rgb(6,78,59),0_14px_36px_rgba(16,185,129,0.35)] transition hover:brightness-105 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(6,78,59)] disabled:pointer-events-none disabled:opacity-40 sm:min-h-[3.5rem] sm:text-lg',
    footIdle:
      'flex items-center justify-center gap-2 rounded-sm border border-emerald-950/45 bg-black/40 px-3 py-2.5 text-center text-[11px] font-bold uppercase tracking-wide text-stone-500',
    footActive:
      'flex items-center justify-center gap-2 rounded-sm border-2 border-emerald-600/45 bg-emerald-950/35 px-3 py-2.5 text-center text-[11px] font-black uppercase tracking-wide text-emerald-200',
    loadingBox:
      'flex items-center justify-center gap-2 rounded-sm border border-emerald-900/35 bg-black/35 px-3 py-3 text-center text-xs font-semibold text-stone-500',
    inviteOutlineBtn:
      'lobby-focus mt-3 flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-sm border-2 border-emerald-700/50 bg-[#101916] px-3 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-emerald-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:border-emerald-500 hover:bg-[#152520] sm:text-sm',
    spinnerAccent: 'text-emerald-500',
    accentIcon: 'text-emerald-300',
    spinnerMuted: 'text-emerald-600',
    loadingEmptyText: 'text-emerald-950/80',
    friendAddBtn:
      'lobby-focus inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border-2 border-emerald-700/50 bg-[#101916] text-emerald-200 transition hover:border-emerald-500 hover:bg-[#152520] disabled:opacity-40',
    spinnerFootActive: 'text-emerald-400',
    spinnerFootIdle: 'text-emerald-700',
    chatScrollBorder: 'border-emerald-900/45',
    chatBubbleSelf: 'ml-4 border-emerald-600/40 bg-emerald-950/35 text-emerald-50',
    chatNameAccent: 'font-black uppercase tracking-wide text-emerald-400/90',
    chatInput:
      'min-w-0 flex-1 rounded-sm border-2 border-emerald-900/50 bg-black/50 px-2 py-2 text-xs text-stone-100 placeholder:text-stone-600 focus:border-emerald-600/55 focus:outline-none disabled:opacity-50',
    chatSendBtn:
      'lobby-focus shrink-0 rounded-sm border-2 border-emerald-600/50 bg-emerald-950/45 px-3 py-2 text-[10px] font-black uppercase tracking-wide text-emerald-100 transition hover:bg-emerald-900/55 disabled:opacity-40',
    modalDivider: 'border-emerald-900/40',
    monoAccent: 'text-emerald-400/90',
    secondaryActionBtn:
      'lobby-focus shrink-0 rounded-sm border-2 border-emerald-600/50 bg-emerald-950/40 px-2 py-1.5 text-[9px] font-black uppercase tracking-wide text-emerald-100 transition hover:bg-emerald-900/50',
    emptyPanel: 'mt-4 rounded-sm border border-emerald-900/35 bg-black/35 px-3 py-4 text-center text-xs text-stone-500',
  },
  belot: {
    page: 'bg-[#0a0812]',
    vignette:
      'pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(ellipse_120%_70%_at_50%_-15%,rgba(167,139,250,0.2),transparent_55%),radial-gradient(ellipse_at_100%_110%,rgba(91,33,182,0.16),transparent_42%)]',
    header:
      'relative z-20 border-b-2 border-violet-800/50 bg-gradient-to-b from-[#14101f] via-[#0f0c18] to-[#080612] px-3 py-2.5 shadow-[0_6px_28px_rgba(0,0,0,0.65),inset_0_1px_0_rgba(167,139,250,0.14)] sm:px-4',
    backBtn:
      'lobby-focus inline-flex h-9 w-9 items-center justify-center rounded-sm border-2 border-violet-800/55 bg-[#15121c] text-violet-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:border-violet-500 hover:bg-[#1c1728]',
    titleKicker: 'text-[10px] font-black uppercase tracking-[0.22em] text-violet-400',
    titleMain:
      'text-sm font-black uppercase tracking-[0.08em] text-violet-100 drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)] sm:text-base',
    statusChip:
      'inline-flex items-center gap-1 rounded-sm border-2 border-violet-700/50 bg-black/55 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-violet-300',
    panel:
      'relative z-10 overflow-hidden rounded-sm border-2 border-violet-800/45 bg-gradient-to-b from-[#18151f] via-[#110e18] to-[#0b0912] p-4 shadow-[inset_0_1px_0_rgba(167,139,250,0.1),0_16px_48px_rgba(0,0,0,0.55)]',
    panelTopSheen:
      'pointer-events-none absolute inset-x-3 top-0 h-px bg-gradient-to-r from-transparent via-violet-400/45 to-transparent',
    introTitle: 'text-[10px] font-black uppercase tracking-[0.2em] text-violet-400',
    logo: 'h-11 w-auto shrink-0 object-contain opacity-95 drop-shadow-[0_0_12px_rgba(167,139,250,0.38)]',
    divider: 'my-4 h-px bg-gradient-to-r from-transparent via-violet-800/40 to-transparent',
    codeBox:
      'rounded-sm border-2 border-violet-800/50 bg-black/55 px-4 py-4 text-center shadow-[inset_0_0_28px_rgba(139,92,246,0.1),0_0_24px_rgba(91,33,182,0.15)]',
    codeLabel: 'text-[10px] font-black uppercase tracking-[0.28em] text-violet-500',
    codeValue:
      'mt-1.5 font-mono text-2xl font-black tracking-[0.24em] text-violet-200 sm:text-3xl [text-shadow:0_0_18px_rgba(167,139,250,0.4)]',
    statRow: 'text-[11px] font-bold uppercase tracking-wider text-stone-500',
    statAccent: 'font-black text-violet-300 tabular-nums',
    playerRow:
      'flex items-center gap-3 border border-violet-950/65 bg-gradient-to-r from-[#16131f] to-[#0e0c16] py-2.5 pl-2 pr-3 shadow-[inset_4px_0_0_0_rgba(139,92,246,0.85)]',
    avatarWrap:
      'flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-sm border-2 border-violet-700/50 bg-[#0b0912] text-sm font-black text-violet-200 shadow-[inset_0_0_14px_rgba(0,0,0,0.85)]',
    name: 'min-w-0 truncate text-sm font-bold uppercase tracking-wide text-stone-100',
    hostBadge:
      'rounded-sm border border-violet-700/45 bg-violet-950/65 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-violet-200',
    readyBadge:
      'inline-flex items-center gap-0.5 rounded-sm border border-fuchsia-500/45 bg-fuchsia-950/45 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-fuchsia-300',
    notReady: 'text-[10px] font-semibold uppercase tracking-wide text-stone-600',
    kickBtn:
      'lobby-focus inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border-2 border-red-700/55 bg-red-950/50 text-red-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:bg-red-950/80 disabled:opacity-40',
    readyBtnOn:
      'lobby-focus flex w-full min-h-[3.25rem] items-center justify-center rounded-sm border-2 border-violet-800 bg-gradient-to-b from-stone-700 via-stone-800 to-stone-950 px-4 py-4 text-base font-black uppercase tracking-[0.12em] text-violet-200 shadow-[0_4px_0_rgb(30,22,45),0_10px_28px_rgba(0,0,0,0.45)] transition hover:brightness-110 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(30,22,45)] disabled:pointer-events-none disabled:opacity-40 sm:min-h-[3.5rem] sm:text-lg',
    readyBtnOff:
      'lobby-focus flex w-full min-h-[3.25rem] items-center justify-center rounded-sm border-2 border-violet-300/85 bg-gradient-to-b from-violet-300 via-violet-500 to-violet-950 px-4 py-4 text-base font-black uppercase tracking-[0.15em] text-stone-950 shadow-[0_4px_0_rgb(76,29,149),0_14px_36px_rgba(139,92,246,0.38)] transition hover:brightness-105 active:translate-y-0.5 active:shadow-[0_2px_0_rgb(76,29,149)] disabled:pointer-events-none disabled:opacity-40 sm:min-h-[3.5rem] sm:text-lg',
    footIdle:
      'flex items-center justify-center gap-2 rounded-sm border border-violet-950/45 bg-black/40 px-3 py-2.5 text-center text-[11px] font-bold uppercase tracking-wide text-stone-500',
    footActive:
      'flex items-center justify-center gap-2 rounded-sm border-2 border-violet-600/45 bg-violet-950/35 px-3 py-2.5 text-center text-[11px] font-black uppercase tracking-wide text-violet-200',
    loadingBox:
      'flex items-center justify-center gap-2 rounded-sm border border-violet-900/35 bg-black/35 px-3 py-3 text-center text-xs font-semibold text-stone-500',
    inviteOutlineBtn:
      'lobby-focus mt-3 flex w-full min-h-[2.75rem] items-center justify-center gap-2 rounded-sm border-2 border-violet-700/50 bg-[#15121c] px-3 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-violet-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] transition hover:border-violet-500 hover:bg-[#1c1728] sm:text-sm',
    spinnerAccent: 'text-violet-400',
    accentIcon: 'text-violet-300',
    spinnerMuted: 'text-violet-600',
    loadingEmptyText: 'text-violet-950/80',
    friendAddBtn:
      'lobby-focus inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border-2 border-violet-700/50 bg-[#15121c] text-violet-200 transition hover:border-violet-500 hover:bg-[#1c1728] disabled:opacity-40',
    spinnerFootActive: 'text-violet-300',
    spinnerFootIdle: 'text-violet-700',
    chatScrollBorder: 'border-violet-900/45',
    chatBubbleSelf: 'ml-4 border-violet-600/40 bg-violet-950/35 text-violet-50',
    chatNameAccent: 'font-black uppercase tracking-wide text-violet-400/90',
    chatInput:
      'min-w-0 flex-1 rounded-sm border-2 border-violet-900/50 bg-black/50 px-2 py-2 text-xs text-stone-100 placeholder:text-stone-600 focus:border-violet-600/55 focus:outline-none disabled:opacity-50',
    chatSendBtn:
      'lobby-focus shrink-0 rounded-sm border-2 border-violet-600/50 bg-violet-950/45 px-3 py-2 text-[10px] font-black uppercase tracking-wide text-violet-100 transition hover:bg-violet-900/55 disabled:opacity-40',
    modalDivider: 'border-violet-900/40',
    monoAccent: 'text-violet-400/90',
    secondaryActionBtn:
      'lobby-focus shrink-0 rounded-sm border-2 border-violet-600/50 bg-violet-950/40 px-2 py-1.5 text-[9px] font-black uppercase tracking-wide text-violet-100 transition hover:bg-violet-900/50',
    emptyPanel: 'mt-4 rounded-sm border border-violet-900/35 bg-black/35 px-3 py-4 text-center text-xs text-stone-500',
  },
};

function buildLivePresenceMaps(liveLobbyUsers, serverPresenceByUserId, acceptedFriends) {
  const byId = new Map();
  const byName = new Map();
  const server =
    serverPresenceByUserId && typeof serverPresenceByUserId === 'object' ? serverPresenceByUserId : null;
  let hasServer = false;
  if (server && Object.keys(server).length) {
    hasServer = true;
    for (const [uid, row] of Object.entries(server)) {
      const id = String(uid || '').trim();
      const st = String(row?.state || '').toLowerCase();
      if (!id || !['online', 'in_game', 'offline'].includes(st)) continue;
      byId.set(id, st);
    }
  }
  (Array.isArray(liveLobbyUsers) ? liveLobbyUsers : []).forEach((u) => {
    const uid = String(u?.id || '').trim();
    if (!uid) return;
    const last = String(u?.lastPlayedLabel || '').toLowerCase();
    const fromChat = last.includes('игр') ? 'in_game' : 'online';
    if (!hasServer || !byId.has(uid)) {
      byId.set(uid, fromChat);
    }
  });
  const friends = Array.isArray(acceptedFriends) ? acceptedFriends : [];
  for (const f of friends) {
    const id = String(f?.id || '').trim();
    const uname = String(f?.username || '').trim().toLowerCase();
    if (!id || !uname) continue;
    const st = byId.get(id);
    if (st) byName.set(uname, st);
  }
  return { byId, byName };
}

/** Меньше = выше в списке: онлайн, в игре, оффлайн. */
function friendOnlineSortKey(friend, byId, byName) {
  const p =
    byId.get(String(friend.id || '')) ||
    byName.get(String(friend.username || '').trim().toLowerCase());
  if (p === 'online') return 0;
  if (p === 'in_game') return 1;
  if (p === 'offline') return 2;
  const last = String(friend.lastPlayedLabel || '').toLowerCase();
  if (last.includes('сети') || last.includes('онлайн')) return 0;
  if (last.includes('игр')) return 1;
  return 2;
}

function inviteFriendPresenceBadge(friend, byId, byName) {
  const p =
    byId.get(String(friend.id || '')) ||
    byName.get(String(friend.username || '').trim().toLowerCase());
  if (p === 'in_game') {
    return { text: 'В игре', className: 'border-violet-500/45 bg-violet-950/50 text-violet-200' };
  }
  if (p === 'online') {
    return { text: 'Онлайн', className: 'border-emerald-500/45 bg-emerald-950/45 text-emerald-200' };
  }
  if (p === 'offline') {
    return { text: 'Оффлайн', className: 'border-slate-600/45 bg-black/40 text-stone-500' };
  }
  const last = String(friend.lastPlayedLabel || '').toLowerCase();
  if (last.includes('сети') || last.includes('онлайн')) {
    return { text: 'Онлайн', className: 'border-emerald-500/45 bg-emerald-950/45 text-emerald-200' };
  }
  if (last.includes('игр')) {
    return { text: 'В игре', className: 'border-violet-500/45 bg-violet-950/50 text-violet-200' };
  }
  return { text: 'Оффлайн', className: 'border-slate-600/45 bg-black/40 text-stone-500' };
}

const OnlineLobby = ({
  user,
  mode = 'seka',
  accessToken,
  onGameStart,
  onStartGame,
  onBack,
  onOpenGifts,
  onOpenFriends,
  giftsNavBadgeCount = 0,
  friendsNavBadgeCount = 0,
  /** Вход из хаба «Столы»: тот же сценарий, что кнопка «Войти» по коду. */
  hubPendingJoin = null,
  onHubPendingJoinConsumed,
  /** С хаба «Создать»: после подключения к онлайн-лобби открыть модалку создания. */
  hubOpenCreateModal = false,
  onHubOpenCreateModalConsumed,
  /** Принятые друзья из `mailFriendRequests` (id, username, level, avatarUrl). */
  acceptedFriends = [],
  /** Кто из друзей недавно писал в глобальный чат — доп. подсказка, если нет снимка REST presence. */
  liveLobbyUsers = [],
  /** Снимок `/api/friends/presence`: state + detail по id друга. */
  friendServerPresenceByUserId = {},
  /** Заявка в друзья игроку из списка ожидания (как из профиля в глобальном чате). */
  onSendFriendRequest,
  /** Входящая заявка из того же Colyseus-лобби (сервер шлёт только адресату). */
  onLobbyFriendRequestInbound,
  /** Отправителю: адресат принял/отклонил заявку (тот же канал, что входящая). */
  onLobbyFriendRequestResolved,
  /** Входящий/исходящий social invite (вызов на игру). */
  onSocialInvite,
  /** После reconnected Colyseus: синхронизация заявок/друзей с REST (хаб отключён в online-lobby). */
  onSocialTransportReconnected,
  /** Игровая почта (тот же коллбэч, что в хабе). */
  onOpenMail,
  mailUnreadCount = 0,
  reconnectWaitContext = null,
}) => {
  const authUserId = String(user?.id || '').trim();
  const authUsername = String(user?.username || 'Игрок');
  const authClan = String(user?.clan || '');
  const authLevel = Math.max(1, Math.floor(Number(user?.level) || 1));
  const authAvatarUrl = String(user?.avatarUrl || '').trim();
  const playerName = (user?.username || '').trim();
  const theme = ONLINE_THEME[mode] || ONLINE_THEME.seka;
  const modeIconAuraClass =
    mode === 'poker' ? 'bg-emerald-300/25' : mode === 'belot' ? 'bg-violet-300/25' : 'bg-amber-300/25';
  const [roomCode, setRoomCode] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [roomsList, setRoomsList] = useState([]);
  const [belotVariantUi, setBelotVariantUi] = useState('armenian');
  const [roomSearch, setRoomSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [createStakeIndex, setCreateStakeIndex] = useState(2);
  const [createAnteIndex, setCreateAnteIndex] = useState(1);
  const selectedTeamStake = ROOM_STAKE_OPTIONS[createStakeIndex];
  const selectedPlayerStake = Math.floor(selectedTeamStake / 2);
  const maxAnteIdxLobby = useMemo(() => {
    const cap = selectedPlayerStake;
    let idx = 0;
    for (let i = 0; i < SEKA_ROOM_ANTE_OPTIONS.length; i++) {
      if (SEKA_ROOM_ANTE_OPTIONS[i] <= cap) idx = i;
      else break;
    }
    return idx;
  }, [selectedPlayerStake]);
  useEffect(() => {
    setCreateAnteIndex((i) => Math.min(i, maxAnteIdxLobby));
  }, [maxAnteIdxLobby]);
  const selectedAnte = SEKA_ROOM_ANTE_OPTIONS[Math.min(createAnteIndex, maxAnteIdxLobby)];
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createModalPreset, setCreateModalPreset] = useState(null);
  const [joinPasswordModal, setJoinPasswordModal] = useState(null);
  const [joinPasswordDraft, setJoinPasswordDraft] = useState('');
  const [inviteFriendModalOpen, setInviteFriendModalOpen] = useState(false);
  const [waitingChatMessages, setWaitingChatMessages] = useState([]);
  const [waitingChatDraft, setWaitingChatDraft] = useState('');
  const roomTheme = ROOM_THEME[mode] || ROOM_THEME.seka;
  const isSekaMode = mode === 'seka';
  const isBelotMode = mode === 'belot';

  useEffect(() => {
    if (!isBelotMode) return;
    setBelotVariantUi((prev) => (prev === 'moldovan' ? 'moldovan' : 'armenian'));
  }, [isBelotMode]);

  const anteFillPercentLobby =
    maxAnteIdxLobby <= 0 ? 100 : (Math.min(createAnteIndex, maxAnteIdxLobby) / maxAnteIdxLobby) * 100;

  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [transport, setTransport] = useState(null);
  const [toasts, setToasts] = useState([]);
  const reconnectWaitDeadlineAtMs = Number(reconnectWaitContext?.reconnectWaitDeadlineAtMs || 0);
  const [reconnectWaitSecLeft, setReconnectWaitSecLeft] = useState(null);
  useEffect(() => {
    if (!Number.isFinite(reconnectWaitDeadlineAtMs) || reconnectWaitDeadlineAtMs <= 0) {
      setReconnectWaitSecLeft(null);
      return undefined;
    }
    const tick = () => {
      const ms = reconnectWaitDeadlineAtMs - Date.now();
      setReconnectWaitSecLeft(ms <= 0 ? 0 : Math.ceil(ms / 1000));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [reconnectWaitDeadlineAtMs]);
  const onGameStartRef = useRef(onGameStart);
  const onStartGameRef = useRef(onStartGame);
  const gameStartHandledByTableRef = useRef(new Map());
  /** Дедуп тоста между перезапусками эффекта (ref не сбрасывается). */
  const lastReconnectToastAtRef = useRef(0);
  useEffect(() => {
    onGameStartRef.current = onGameStart;
    onStartGameRef.current = onStartGame;
  }, [onGameStart, onStartGame]);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const pushAppToast = useCallback((message, type = 'info') => {
    setToasts((prev) => [
      ...prev,
      { id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, message, type },
    ]);
  }, []);

  const notifyFriendRequestAfterSend = useCallback(
    async (target) => {
      if (typeof onSendFriendRequest !== 'function' || !target) return;
      const result = await Promise.resolve(onSendFriendRequest(target));
      if ((result === 'sent' || result === 'resent') && connected && transport?.emit) {
        const tid = String(target.id || '').trim();
        if (tid) transport.emit('FRIEND_REQUEST_NOTIFY', { targetUserId: tid });
      }
    },
    [onSendFriendRequest, connected, transport]
  );
  const normalizedRooms = useMemo(
    () =>
      (roomsList || [])
        .map((room) => {
          if (typeof room === 'string') {
            return {
              code: room,
              name: '',
              stake: ROOM_STAKE_OPTIONS[0],
              ante: 0,
              currency: 'seka',
              maxPlayers: 2,
              seated: 0,
              players: [],
              isPrivate: false,
              hostUserId: '',
              readyUserIds: [],
              playerProfiles: {},
            };
          }
          if (!room || typeof room !== 'object') return null;
          const code = String(room.roomCode ?? room.code ?? room.id ?? '').trim();
          const roomName = String(room.name ?? '').trim();
          const stakeRaw = Number(room.stake ?? room.buyIn ?? room.minBuyIn ?? ROOM_STAKE_OPTIONS[0]);
          const maxPlayers = Math.max(2, Math.min(9, Number(room.maxPlayers ?? 2)));
          const seated = Number(room.seated ?? (Array.isArray(room.players) ? room.players.length : 0));
          const currency = room.currency === 'bucks' ? 'bucks' : 'seka';
          const anteRaw = Number(room.ante ?? 0);
          const hostUserId = String(room.hostUserId ?? '');
          const readyUserIds = Array.isArray(room.readyUserIds)
            ? room.readyUserIds.map((id) => String(id))
            : [];
          const players = Array.isArray(room.players) ? room.players.map((id) => String(id)) : [];
          const rawProf = room.playerProfiles && typeof room.playerProfiles === 'object' ? room.playerProfiles : {};
          const playerProfiles = {};
          for (const [k, v] of Object.entries(rawProf)) {
            if (!v || typeof v !== 'object') continue;
            playerProfiles[String(k)] = {
              username: String(v.username || k),
              level: Math.max(1, Math.floor(Number(v.level) || 1)),
              avatarUrl: String(v.avatarUrl || '').trim(),
            };
          }
          const belotTs = Number(room.belotTargetScore);
          return {
            code,
            name: roomName,
            stake:
              Number.isFinite(stakeRaw) && stakeRaw > 0
                ? Math.max(ROOM_STAKE_OPTIONS[0], Math.floor(stakeRaw))
                : ROOM_STAKE_OPTIONS[0],
            ante: Number.isFinite(anteRaw) && anteRaw > 0 ? Math.floor(anteRaw) : 0,
            currency,
            maxPlayers,
            seated: Number.isFinite(seated) ? seated : 0,
            players,
            isPrivate: Boolean(room.isPrivate),
            belotVariant: String(room.belotVariant || '').trim().toLowerCase(),
            belotTargetScore:
              mode === 'belot' ? (belotTs === 5010 || belotTs === 501 ? 5010 : 10010) : undefined,
            belotShulerEnabled: mode === 'belot' ? Boolean(room.belotShulerEnabled) : false,
            hostUserId,
            readyUserIds,
            playerProfiles,
          };
        })
        .filter((room) => room && room.code),
    [roomsList, mode]
  );
  const filteredRooms = useMemo(() => {
    const q = roomSearch.trim().toLowerCase();
    return normalizedRooms.filter((room) => {
      if (q) {
        const byCode = room.code.toLowerCase().includes(q);
        const byName = String(room.name || '').toLowerCase().includes(q);
        if (!byCode && !byName) return false;
      }
      // For belot, keep room discovery simple: do not hide tables by local stake slider.
      if (mode !== 'belot' && Number(room.stake) !== selectedPlayerStake) return false;
      if (mode === 'seka') {
        const ra = Number(room.ante || 0);
        if (ra > 0 && ra !== selectedAnte) return false;
      }
      if (mode === 'belot') {
        const roomVariant = String(room.belotVariant || '').trim().toLowerCase() || 'armenian';
        // Treat Open 1x1 as part of Moldovan list in lobby filtering.
        if (roomVariant === 'open_duell') {
          if (belotVariantUi !== 'moldovan') return false;
        } else if (roomVariant !== belotVariantUi) {
          return false;
        }
      }
      return true;
    });
  }, [normalizedRooms, roomSearch, selectedPlayerStake, selectedAnte, mode, belotVariantUi]);

  const waitingRoomSnapshot = useMemo(() => {
    if (!waiting || !roomCode) return null;
    const code = String(roomCode).toUpperCase();
    return normalizedRooms.find((r) => r.code === code) || null;
  }, [waiting, roomCode, normalizedRooms]);

  const inviteFriendPresenceMaps = useMemo(
    () => buildLivePresenceMaps(liveLobbyUsers, friendServerPresenceByUserId, acceptedFriends),
    [liveLobbyUsers, friendServerPresenceByUserId, acceptedFriends]
  );

  const sortedInviteFriends = useMemo(() => {
    const { byId, byName } = inviteFriendPresenceMaps;
    const raw = Array.isArray(acceptedFriends) ? acceptedFriends : [];
    return [...raw].sort((a, b) => {
      const ra = friendOnlineSortKey(a, byId, byName);
      const rb = friendOnlineSortKey(b, byId, byName);
      if (ra !== rb) return ra - rb;
      return String(a.username || '').localeCompare(String(b.username || ''), 'ru');
    });
  }, [acceptedFriends, inviteFriendPresenceMaps]);

  const openInviteFriendModal = useCallback(() => {
    setInviteFriendModalOpen(true);
  }, []);

  const copyRoomInviteForFriend = useCallback(
    async (friend) => {
      const code = String(roomCode || '').trim().toUpperCase();
      if (!code) {
        pushAppToast('Нет кода комнаты', 'warning');
        return;
      }
      const tableName = String(waitingRoomSnapshot?.name || theme.modeLabel).trim();
      const body = `${theme.modeLabel}: стол «${tableName}». Код комнаты: ${code}`;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(body);
        } else if (typeof window !== 'undefined') {
          window.prompt('Скопируйте текст приглашения:', body);
        }
        const who = String(friend?.username || 'корешу').trim();
        pushAppToast(`Текст скопирован — отправьте ${who}`, 'success');
      } catch {
        pushAppToast('Не удалось скопировать в буфер', 'error');
      }
    },
    [roomCode, waitingRoomSnapshot, theme.modeLabel, pushAppToast]
  );

  useEffect(() => {
    if (!authUserId) {
      setConnected(false);
      setError('Нужна авторизация');
      return undefined;
    }
    let cancelled = false;
    let activeTransport = null;

    const pushToast = (message, type = 'info') => {
      if (cancelled) return;
      setToasts((prev) => [
        ...prev,
        { id: `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`, message, type },
      ]);
    };

    const onConnect = () => {
      console.log('Подключен к серверу');
      gameStartHandledByTableRef.current.clear();
      setConnected(true);
      setError('');
      activeTransport.emit('GET_ROOM_LIST');
    };

    const onDisconnect = (payload) => {
      console.log('Отключен от сервера');
      setConnected(false);
      if (payload?.intentional) return;
      if (payload?.duplicateSession) {
        pushToast(duplicateSessionUserMessage('lobby'), 'warning');
        return;
      }
      pushToast(finalDisconnectUserMessage('lobby', payload?.error), 'error');
    };

    const onReconnected = (payload) => {
      setConnected(true);
      setError('');
      // После реконнекта прошлые дедуп-ключи старта могут стать невалидными.
      gameStartHandledByTableRef.current.clear();
      const now = Date.now();
      if (now - lastReconnectToastAtRef.current < 8000) {
        activeTransport?.emit('GET_ROOM_LIST');
        if (typeof onSocialTransportReconnected === 'function') onSocialTransportReconnected();
        return;
      }
      lastReconnectToastAtRef.current = now;
      pushToast(
        reconnectSuccessUserMessage({ context: 'lobby', fallbackJoin: isReconnectFallbackJoin(payload) }),
        'success'
      );
      activeTransport?.emit('GET_ROOM_LIST');
      if (typeof onSocialTransportReconnected === 'function') onSocialTransportReconnected();
    };

    const onRoomCreated = (data) => {
      setCreateSubmitting(false);
      setCreateModalOpen(false);
      setRoomCode(data.roomCode);
      setWaiting(true);
      setError('');
      console.log('Комната создана:', data);
    };

    const onJoinError = (payload) => {
      const msg =
        payload && typeof payload === 'object'
          ? String(payload.message || payload.code || 'Ошибка присоединения')
          : String(payload || 'Ошибка присоединения');
      const code = payload && typeof payload === 'object' ? String(payload.code || '') : '';
      const failedCode = payload && typeof payload === 'object' ? String(payload.roomCode || '').trim() : '';
      const inviteOnly = Boolean(payload && typeof payload === 'object' ? payload.inviteOnly : false);
      const retryCode = failedCode.toUpperCase();
      if (inviteOnly && code === 'TABLE_NOT_FOUND' && retryCode) {
        const retries = Number(inviteJoinRetryByCodeRef.current.get(retryCode) || 0);
        if (retries < 1) {
          inviteJoinRetryByCodeRef.current.set(retryCode, retries + 1);
          setError('Комната обновляется, повторяем вход...');
          setWaiting(true);
          setTimeout(() => {
            activeTransport?.emit('JOIN_ROOM', {
              roomCode: retryCode,
              playerName,
              mode,
              inviteOnly: true,
            });
          }, 250);
          return;
        }
      }
      setWaiting(false);
      setError(
        code === 'INVITE_ACCESS_DENIED' ? 'Эта комната доступна только приглашенному игроку.' : msg
      );
      if (
        !inviteOnly &&
        (code === 'TABLE_NOT_FOUND' || code === 'TABLE_BAD_PASSWORD' || code === 'JOIN_TIMEOUT') &&
        retryCode
      ) {
        setJoinPasswordModal({ code: retryCode });
      }
      console.error('Ошибка присоединения:', msg);
    };

    const onWaitingRoomLost = (payload) => {
      inviteJoinRetryByCodeRef.current.clear();
      gameStartHandledByTableRef.current.clear();
      setWaiting(false);
      setRoomCode('');
      const reason = String(payload?.reason || '');
      pushToast(
        reason === 'not_seated'
          ? 'Вас исключили из комнаты.'
          : 'Комната закрыта или вы больше не за столом.',
        'warning'
      );
    };

    const onTransportError = (payload) => {
      setCreateSubmitting(false);
      const msg =
        payload && typeof payload === 'object'
          ? String(payload.message || payload.code || 'Ошибка лобби')
          : String(payload || 'Ошибка лобби');
      setError(msg);
    };

    const onGameStartEvent = (data) => {
      console.log('Игра начинается:', data);
      const code = String(data?.roomCode || '').trim().toUpperCase();
      const gid = String(data?.gameRoomId || '').trim();
      const prev = code ? String(gameStartHandledByTableRef.current.get(code) || '') : '';
      if (!gid) {
        return;
      }
      if (code && prev) {
        if (prev && gid && prev !== gid) {
          console.warn('[OnlineLobby] GAME_START conflict ignored', { code, prev, gid });
        }
        return;
      }
      if (code) gameStartHandledByTableRef.current.set(code, gid);
      setWaiting(false);
      const cbStart = onGameStartRef.current;
      if (typeof cbStart === 'function') {
        cbStart({
          roomCode: data.roomCode,
          playerName,
          players: data.players,
          playerIndex: data.playerIndex,
          financial: data.financial || null,
          socket: activeTransport,
        });
      }
      const cbGame = onStartGameRef.current;
      if (typeof cbGame === 'function') {
        cbGame({
          ...(data.gameState || {}),
          roomCode: data.roomCode,
          gameRoomId: data.gameRoomId,
          players: data.players,
          playerIndex: data.playerIndex,
          financial: data.financial || null,
        });
      }
    };

    const onRoomListUpdate = (codes) => {
      setRoomsList(codes);
      console.log('Обновлен список комнат:', codes);
    };

    (async () => {
      try {
        activeTransport = await createOnlineLobbyTransport(
          {
            id: authUserId,
            username: authUsername,
            clan: authClan,
            level: authLevel,
            avatarUrl: authAvatarUrl,
          },
          accessToken || undefined,
          mode
        );
        if (cancelled) {
          activeTransport.disconnect?.();
          return;
        }
        setTransport(activeTransport);
        activeTransport.on('connect', onConnect);
        activeTransport.on('disconnect', onDisconnect);
        activeTransport.on('reconnected', onReconnected);
        activeTransport.on('ROOM_CREATED', onRoomCreated);
        activeTransport.on('JOIN_ERROR', onJoinError);
        activeTransport.on('WAITING_ROOM_LOST', onWaitingRoomLost);
        activeTransport.on('GAME_START', onGameStartEvent);
        activeTransport.on('ROOM_LIST_UPDATE', onRoomListUpdate);
        activeTransport.on('error', onTransportError);
        activeTransport.connect?.();
      } catch (e) {
        console.error('Ошибка инициализации online transport:', e);
        setError('Не удалось подключиться к онлайн-сервису');
      }
    })();

    return () => {
      cancelled = true;
      if (activeTransport) {
        activeTransport.off('connect', onConnect);
        activeTransport.off('disconnect', onDisconnect);
        activeTransport.off('reconnected', onReconnected);
        activeTransport.off('ROOM_CREATED', onRoomCreated);
        activeTransport.off('GAME_START', onGameStartEvent);
        activeTransport.off('ROOM_LIST_UPDATE', onRoomListUpdate);
        activeTransport.off('error', onTransportError);
        activeTransport.off('JOIN_ERROR', onJoinError);
        activeTransport.off('WAITING_ROOM_LOST', onWaitingRoomLost);
        activeTransport.disconnect?.();
      }
      setTransport(null);
    };
  }, [
    playerName,
    accessToken,
    mode,
    authUserId,
    authUsername,
    authClan,
    authLevel,
    authAvatarUrl,
    onSocialTransportReconnected,
  ]);

  useEffect(() => {
    if (!waiting) {
      setWaitingChatMessages([]);
      setWaitingChatDraft('');
    }
  }, [waiting]);

  useEffect(() => {
    if (!transport || !waiting || !roomCode) return undefined;
    const code = String(roomCode).toUpperCase();
    const myId = String(user?.id || '');
    const onTableChat = (payload) => {
      if (String(payload?.tableId || '').toUpperCase() !== code) return;
      setWaitingChatMessages((prev) => {
        const at = Number(payload.at) || Date.now();
        const uid = String(payload.userId || '');
        const txt = String(payload.text || '');
        const id = `wc-${at}-${uid}-${txt.slice(0, 32)}`;
        if (prev.some((p) => p.id === id)) return prev;
        const row = {
          id,
          userId: uid,
          username: String(payload.username || ''),
          text: txt,
          at,
          self: uid === myId,
        };
        return [...prev, row].slice(-120);
      });
    };
    transport.on('TABLE_WAITING_CHAT', onTableChat);
    return () => {
      transport.off('TABLE_WAITING_CHAT', onTableChat);
    };
  }, [transport, waiting, roomCode, user?.id]);

  useEffect(() => {
    if (!transport?.on || typeof onLobbyFriendRequestInbound !== 'function') return undefined;
    const handler = (payload) => {
      onLobbyFriendRequestInbound(payload);
    };
    transport.on('lobby_friend_request', handler);
    return () => {
      transport.off('lobby_friend_request', handler);
    };
  }, [transport, onLobbyFriendRequestInbound]);

  useEffect(() => {
    if (!transport?.on || typeof onLobbyFriendRequestResolved !== 'function') return undefined;
    const handler = (payload) => {
      onLobbyFriendRequestResolved(payload);
    };
    transport.on('lobby_friend_request_resolved', handler);
    return () => {
      transport.off('lobby_friend_request_resolved', handler);
    };
  }, [transport, onLobbyFriendRequestResolved]);

  useEffect(() => {
    if (!transport?.on || typeof onSocialInvite !== 'function') return undefined;
    const handler = (payload) => {
      onSocialInvite(payload);
    };
    transport.on('social_invite', handler);
    return () => {
      transport.off('social_invite', handler);
    };
  }, [transport, onSocialInvite]);

  useEffect(() => {
    const onSendInvite = (ev) => {
      const detail = ev?.detail || {};
      if (!connected || !transport?.emit) return;
      transport.emit('SOCIAL_SEND_INVITE', detail);
    };
    window.addEventListener('seka-social-send-invite', onSendInvite);
    return () => window.removeEventListener('seka-social-send-invite', onSendInvite);
  }, [connected, transport]);

  useEffect(() => {
    const onResolveInvite = (ev) => {
      const detail = ev?.detail || {};
      if (!connected || !transport?.emit) return;
      transport.emit('SOCIAL_RESOLVE_INVITE', detail);
    };
    window.addEventListener('seka-social-resolve-invite', onResolveInvite);
    return () => window.removeEventListener('seka-social-resolve-invite', onResolveInvite);
  }, [connected, transport]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResolve = (e) => {
      const d = e?.detail || {};
      const otherUserId = String(d.otherUserId || '').trim();
      const status = String(d.status || '').toLowerCase();
      if (!otherUserId || (status !== 'accepted' && status !== 'declined')) return;
      if (!connected || !transport?.emit) return;
      try {
        transport.emit('FRIEND_REQUEST_RESOLVE', { otherUserId, status });
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('seka-friend-request-resolve', onResolve);
    return () => window.removeEventListener('seka-friend-request-resolve', onResolve);
  }, [connected, transport]);

  const sendWaitingTableChat = useCallback(() => {
    const text = waitingChatDraft.trim();
    const code = String(roomCode || '').trim().toUpperCase();
    if (!text || !connected || !transport || !code || !waiting) return;
    try {
      setError('');
      transport.emit('TABLE_WAITING_CHAT', { tableId: code, text });
      setWaitingChatDraft('');
    } catch {
      pushAppToast('Не удалось отправить сообщение', 'error');
    }
  }, [waitingChatDraft, connected, transport, roomCode, waiting, pushAppToast]);

  const openCreateModal = useCallback(() => {
    if (!playerName) {
      setError('Не найден никнейм пользователя. Войдите в аккаунт заново.');
      return;
    }
    if (!connected) return;
    setError('');
    const sekaBalance = Math.max(0, Number(user?.balance ?? 0));
    const baseBuyInConfig = { min: 10, max: 1000000, step: 10 };
    const roundedBalanceByStep = Math.floor(sekaBalance / baseBuyInConfig.step) * baseBuyInConfig.step;
    const canAffordMin = sekaBalance >= baseBuyInConfig.min;
    const buyInConfigPreset = {
      ...baseBuyInConfig,
      canAffordMin,
      max: canAffordMin
        ? Math.max(baseBuyInConfig.min, Math.min(baseBuyInConfig.max, roundedBalanceByStep))
        : baseBuyInConfig.min,
    };
    const nextBuyIn = normalizeBuyInValue(selectedPlayerStake, buyInConfigPreset);
    const nextAnte = Math.min(
      SEKA_ROOM_ANTE_OPTIONS[Math.min(createAnteIndex, maxAnteIdxLobby)],
      nextBuyIn
    );
    setCreateModalPreset({
      name: '',
      currency: 'seka',
      maxPlayers:
        mode === 'poker' ? 6 : mode === 'belot' ? (String(belotVariantUi).toLowerCase() === 'open_duell' ? 2 : 4) : 2,
      buyIn: nextBuyIn,
      ante: mode === 'seka' ? nextAnte : 10,
      belotVariant: mode === 'belot' ? belotVariantUi : undefined,
      belotTargetScore: mode === 'belot' ? 10010 : undefined,
      isPrivate: false,
      roomPassword: '',
    });
    setCreateModalOpen(true);
  }, [playerName, connected, selectedPlayerStake, createAnteIndex, maxAnteIdxLobby, mode, user?.balance, belotVariantUi]);

  const handleJoinRoom = useCallback(
    (code, roomPassword = '', options = {}) => {
      if (playerName && code?.trim()) {
        setError('');
        const c = code.trim().toUpperCase();
        // Новый join по коду — не наследуем старый dedup-старт этой комнаты.
        gameStartHandledByTableRef.current.delete(c);
        setRoomCode(c);
        setWaiting(true);
        const pw = String(roomPassword || '').trim();
        transport?.emit('JOIN_ROOM', {
          roomCode: c,
          playerName,
          mode,
          inviteOnly: Boolean(options?.inviteOnly),
          ...(pw ? { roomPassword: pw } : {}),
        });
        return true;
      } else {
        setError('Комната не выбрана или не найден никнейм игрока.');
        return false;
      }
    },
    [playerName, transport, mode]
  );

  const hubAutoJoinHandledRef = useRef(false);
  const hubPendingConsumedRef = useRef(false);
  const inviteJoinRetryByCodeRef = useRef(new Map());
  useEffect(() => {
    const raw = hubPendingJoin?.roomCode ? String(hubPendingJoin.roomCode).trim().toUpperCase() : '';
    if (!raw) {
      hubAutoJoinHandledRef.current = false;
      hubPendingConsumedRef.current = false;
      inviteJoinRetryByCodeRef.current.clear();
      return;
    }
    /** Новый intent от родителя — снова разрешаем авто-JOIN (в т.ч. возврат со стола с тем же кодом). */
    hubAutoJoinHandledRef.current = false;
    hubPendingConsumedRef.current = false;
  }, [hubPendingJoin?.roomCode, hubPendingJoin?.hubJoinNonce]);

  useEffect(() => {
    const codeRaw = hubPendingJoin && String(hubPendingJoin.roomCode || '').trim();
    if (!codeRaw) return;
    if (!connected || !transport || !playerName) return;
    if (hubAutoJoinHandledRef.current) return;
    const code = codeRaw.toUpperCase();
    const hubPw = String(hubPendingJoin.roomPassword || '').trim();
    const isInviteOnly = Boolean(hubPendingJoin.inviteOnly);
    if (hubPendingJoin.isPrivate && hubPw && !isInviteOnly) {
      const sent = handleJoinRoom(code, hubPw, { inviteOnly: false });
      if (sent) hubAutoJoinHandledRef.current = true;
    } else if (hubPendingJoin.isPrivate && !isInviteOnly) {
      setJoinPasswordModal({ code });
      hubAutoJoinHandledRef.current = true;
      onHubPendingJoinConsumed?.();
    } else {
      const sent = handleJoinRoom(code, '', { inviteOnly: isInviteOnly });
      if (sent) hubAutoJoinHandledRef.current = true;
    }
  }, [hubPendingJoin, connected, transport, playerName, handleJoinRoom, onHubPendingJoinConsumed]);

  /** Снимаем `hubPendingJoin` в родителе только когда локально уже waiting и код совпал — иначе Strict Mode / гонка съедали pending до setWaiting. */
  useEffect(() => {
    const pendingCode = hubPendingJoin?.roomCode ? String(hubPendingJoin.roomCode).trim().toUpperCase() : '';
    const localCode = String(roomCode || '').trim().toUpperCase();
    if (!pendingCode || !waiting || pendingCode !== localCode) return;
    if (hubPendingConsumedRef.current) return;
    hubPendingConsumedRef.current = true;
    onHubPendingJoinConsumed?.();
  }, [waiting, roomCode, hubPendingJoin?.roomCode, onHubPendingJoinConsumed]);

  const hubOpenCreateHandledRef = useRef(false);
  useEffect(() => {
    if (!hubOpenCreateModal) {
      hubOpenCreateHandledRef.current = false;
    }
  }, [hubOpenCreateModal]);

  useEffect(() => {
    if (!hubOpenCreateModal) return;
    if (!connected || !playerName) return;
    if (hubOpenCreateHandledRef.current) return;
    hubOpenCreateHandledRef.current = true;
    openCreateModal();
    onHubOpenCreateModalConsumed?.();
  }, [hubOpenCreateModal, connected, playerName, openCreateModal, onHubOpenCreateModalConsumed]);

  const toggleWaitingReady = useCallback(
    (ready) => {
      const c = String(roomCode || '').toUpperCase();
      if (!c) return;
      transport?.emit('SET_TABLE_READY', { tableId: c, ready });
    },
    [transport, roomCode]
  );

  const kickWaitingPlayer = useCallback(
    (targetUserId) => {
      const c = String(roomCode || '').toUpperCase();
      const tid = String(targetUserId || '');
      if (!c || !tid) return;
      transport?.emit('KICK_FROM_TABLE', { tableId: c, targetUserId: tid });
    },
    [transport, roomCode]
  );

  const requestJoinRoom = useCallback(
    (room) => {
      if (!playerName || !connected) return;
      if (room.isPrivate) {
        setJoinPasswordModal({ code: room.code });
        setJoinPasswordDraft('');
        return;
      }
      handleJoinRoom(room.code);
    },
    [playerName, connected, handleJoinRoom]
  );

  const handleBack = () => {
    const code = String(roomCode || '').trim().toUpperCase();
    const leaveSeat = Boolean(waiting && code && transport);
    if (leaveSeat) {
      gameStartHandledByTableRef.current.delete(code);
      try {
        transport.emit('LEAVE_TABLE', { tableId: code });
      } catch (e) {
        console.warn('[OnlineLobby] LEAVE_TABLE failed', e);
      }
    }
    const finish = () => {
      transport?.disconnect?.();
      onBack();
    };
    /** Дать сокету отправить leave-table до consented disconnect (как «Выйти» в хабе). */
    if (leaveSeat) setTimeout(finish, 0);
    else finish();
  };
    
  if (waiting) {
    const w = PUBG_WAITING_BY_MODE[mode] || PUBG_WAITING_BY_MODE.seka;
    const waitingUid = String(user?.id || '');
    const waitingOrder = waitingRoomSnapshot?.players?.length ? waitingRoomSnapshot.players : [];
    const waitingMaxP = waitingRoomSnapshot?.maxPlayers ?? 2;
    const waitingFilled = waitingOrder.length;
    const waitingReadySet = new Set(waitingRoomSnapshot?.readyUserIds || []);
    const waitingReadyCount = waitingOrder.filter((id) => waitingReadySet.has(String(id))).length;
    const waitingTableComplete = waitingFilled === waitingMaxP && waitingMaxP > 0;
    const waitingAllReady =
      waitingTableComplete && waitingReadyCount >= waitingFilled && waitingFilled > 0;
    const waitingSelfReady = waitingUid ? waitingReadySet.has(waitingUid) : false;
    const waitingShowReadyCta = Boolean(
      waitingRoomSnapshot &&
        waitingTableComplete &&
        waitingUid &&
        waitingOrder.some((id) => String(id) === waitingUid)
    );
    const waitingIsHost =
      Boolean(waitingUid) && String(waitingRoomSnapshot?.hostUserId || '') === waitingUid;
    return (
      <div
        className={cn(
          'pt-root relative flex min-h-[100dvh] min-h-0 flex-col overflow-hidden text-foreground',
          w.page
        )}
      >
        <div className="opacity-[0.22]">
          <ArcadeModeBackdrop mode={theme.mode} />
        </div>
        <div className={w.vignette} aria-hidden />
        <header
          className={cn(
            w.header,
            'online-lobby-waiting-header pt-[max(0.5rem,env(safe-area-inset-top,0px))]'
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={handleBack}
              className={w.backBtn}
              aria-label="Назад"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </button>
            <div className="text-center">
              <p className={w.titleKicker}>{theme.modeLabel}</p>
              <h1 className={w.titleMain}>Ожидание</h1>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {typeof onOpenMail === 'function' ? (
                <button
                  type="button"
                  onClick={onOpenMail}
                  className={cn(
                    w.backBtn,
                    'relative !h-9 !w-9 border-white/15 bg-white/[0.06]'
                  )}
                  aria-label={
                    mailUnreadCount > 0 ? `Почта, непрочитано: ${mailUnreadCount}` : 'Почта'
                  }
                >
                  <LobbyHubNotificationIndicator count={mailUnreadCount} layout="absoluteCorner" />
                  <Mail className="relative z-[2] h-[17px] w-[17px]" strokeWidth={2} aria-hidden />
                </button>
              ) : null}
              <span className={w.statusChip} aria-label="Ожидание" title="Ожидание">
                <Loader2 className={cn('h-3.5 w-3.5 animate-spin', w.spinnerAccent)} aria-hidden />
              </span>
            </div>
          </div>
        </header>

        {error ? (
          <div className="relative z-30 mx-auto mt-2 max-w-xl px-3 sm:px-4">
            <div
              role="alert"
              className="rounded-sm border border-red-500/45 bg-red-950/50 px-3 py-2 text-center text-xs font-semibold text-red-100"
            >
              {error}
            </div>
          </div>
        ) : null}
        {reconnectWaitContext ? (
          <div className="relative z-30 mx-auto mt-2 max-w-xl px-3 sm:px-4">
            <div className="rounded-sm border border-amber-400/45 bg-amber-950/55 px-3 py-2 text-xs font-semibold text-amber-100">
              Ожидание реконнекта игрока.
              <span className="ml-1 font-mono tabular-nums">
                {reconnectWaitSecLeft != null ? `Таймер: ${reconnectWaitSecLeft} с` : ''}
              </span>
            </div>
          </div>
        ) : null}

        <main
          className="online-lobby-waiting-scroller safe-scroll-top relative z-10 mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col gap-2 overflow-y-auto overscroll-y-contain px-3 pb-[var(--lobby-bottom-nav-h)] pt-3 [scroll-padding-bottom:var(--lobby-bottom-nav-h)] sm:px-4"
        >
          <section className={cn(w.panel, 'online-lobby-waiting-panel overflow-visible')}>
            <div className={w.panelTopSheen} aria-hidden />
            <div className="relative flex min-w-0 items-center gap-3">
              <img
                src={theme.logoSrc}
                alt=""
                className={cn(w.logo, 'online-lobby-waiting-logo')}
                aria-hidden
                draggable={false}
              />
              <div className="min-w-0 flex-1">
                <p className={cn(w.introTitle, 'mb-0')}>{theme.modeLabel}</p>
                {waitingRoomSnapshot?.name ? (
                  <p
                    className="mt-1 truncate text-sm font-black uppercase tracking-wide text-stone-100 sm:text-base"
                    title={waitingRoomSnapshot.name}
                  >
                    {waitingRoomSnapshot.name}
                  </p>
                ) : null}
              </div>
            </div>

            <div className={cn(w.divider, 'online-lobby-waiting-divider')} />

            <div className={cn(w.codeBox, 'online-lobby-waiting-codebox')}>
              <p className={w.codeLabel}>Код</p>
              <p className={cn(w.codeValue, 'online-lobby-waiting-code')}>
                {roomCode}
              </p>
            </div>

            {waitingShowReadyCta ? (
              <>
                <div className={cn('mt-2 flex flex-wrap items-center justify-between gap-2', w.statRow)}>
                  <span className="tabular-nums">
                    Игроки{' '}
                    <span className={w.statAccent}>
                      {waitingFilled}/{waitingMaxP}
                    </span>
                  </span>
                  <span className="tabular-nums">
                    Готовы{' '}
                    <span className={w.statAccent}>
                      {waitingReadyCount}/{Math.max(waitingFilled, 1)}
                    </span>
                  </span>
                </div>
                <div className="online-lobby-waiting-ready-row online-lobby-waiting-ready-mobile-anchor mt-2 flex min-w-0 flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => toggleWaitingReady(!waitingSelfReady)}
                    disabled={!connected}
                    className={cn(
                      waitingSelfReady ? w.readyBtnOn : w.readyBtnOff,
                      'online-lobby-ready-btn w-full min-w-0 sm:max-w-md sm:self-center',
                      !waitingSelfReady && 'motion-safe:animate-pubg-ready-pulse motion-reduce:animate-none'
                    )}
                  >
                    {waitingSelfReady ? 'Отмена' : 'Готов'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={openInviteFriendModal}
                  className={cn(w.inviteOutlineBtn, 'online-lobby-invite-btn')}
                  aria-label="Пригласить кореша в комнату"
                >
                  <UserPlus className={cn('h-4 w-4 shrink-0', w.accentIcon)} aria-hidden />
                  Пригласить кореша
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={openInviteFriendModal}
                className={cn(w.inviteOutlineBtn, 'online-lobby-invite-btn')}
                aria-label="Пригласить кореша в комнату"
              >
                <UserPlus className={cn('h-4 w-4 shrink-0', w.accentIcon)} aria-hidden />
                Пригласить кореша
              </button>
            )}

            <div className={cn('space-y-3', waitingShowReadyCta ? 'mt-3' : 'mt-4')}>
              {!waitingShowReadyCta ? (
                <div className={cn('flex flex-wrap items-center justify-between gap-2', w.statRow)}>
                  <span className="tabular-nums">
                    Игроки{' '}
                    <span className={w.statAccent}>
                      {waitingFilled}/{waitingMaxP}
                    </span>
                  </span>
                  <span className="tabular-nums">
                    Готовы{' '}
                    <span className={w.statAccent}>
                      {waitingReadyCount}/{Math.max(waitingFilled, 1)}
                    </span>
                  </span>
                </div>
              ) : null}

              {!waitingRoomSnapshot ? (
                <p className={w.loadingBox} role="status" aria-label="Загрузка">
                  <Loader2 className={cn('h-3.5 w-3.5 shrink-0 animate-spin', w.spinnerMuted)} aria-hidden />
                </p>
              ) : waitingOrder.length === 0 ? (
                <p className={cn(w.loadingBox, w.loadingEmptyText)}>Нет данных</p>
              ) : (
                <ul className="space-y-2">
                  {waitingOrder.map((pid, idx) => {
                    const id = String(pid);
                    const isSelf = waitingUid && id === waitingUid;
                    const ready = waitingReadySet.has(id);
                    const hostId = String(waitingRoomSnapshot.hostUserId || '');
                    const isRowHost = hostId && id === hostId;
                    const profiles = waitingRoomSnapshot.playerProfiles || {};
                    const prof = profiles[id] || {};
                    const pAvatar = String((isSelf && user?.avatarUrl) || prof.avatarUrl || '').trim();
                    const displayName = isSelf
                      ? playerName || prof.username || 'Вы'
                      : prof.username || `Игрок ${idx + 1}`;
                    const pLevel = Math.max(
                      1,
                      Math.floor(
                        isSelf
                          ? Number(user?.level) || Number(prof.level) || 1
                          : Number(prof.level) || 1
                      )
                    );
                    const initial = (displayName || 'И').charAt(0).toUpperCase();
                    return (
                      <li key={`${id}-${idx}`} className={w.playerRow}>
                        <div className={w.avatarWrap}>
                          {pAvatar ? (
                            <img
                              src={pAvatar}
                              alt=""
                              className="h-full w-full object-cover"
                              draggable={false}
                            />
                          ) : (
                            initial
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <p className={w.name}>{displayName}</p>
                            <OnlineLobbyPlayerRankBadge level={pLevel} />
                          </div>
                          <p className="mt-0.5 flex flex-wrap items-center gap-1.5">
                            {isRowHost ? <span className={w.hostBadge}>Хост</span> : null}
                            {ready ? (
                              <span className={w.readyBadge}>
                                <Check className="h-3 w-3" aria-hidden />
                                Ок
                              </span>
                            ) : (
                              <span className={w.notReady}>—</span>
                            )}
                          </p>
                        </div>
                        {isSelf ? null : (
                          <div className="flex shrink-0 items-center gap-1">
                            {typeof onSendFriendRequest === 'function' ? (
                              <button
                                type="button"
                                onClick={() =>
                                  void notifyFriendRequestAfterSend({
                                    id,
                                    username: String(prof.username || displayName || id).trim() || id,
                                    clan: 'Без клана',
                                    level: pLevel,
                                    avatarUrl: pAvatar || null,
                                  })
                                }
                                disabled={!connected || !user?.id}
                                title="Закорефаниться"
                                aria-label={`Запрос закорефаниться: ${displayName}`}
                                className={w.friendAddBtn}
                              >
                                <UserPlus className="h-4 w-4" aria-hidden />
                              </button>
                            ) : null}
                            {waitingIsHost ? (
                              <button
                                type="button"
                                onClick={() => kickWaitingPlayer(id)}
                                disabled={!connected}
                                title="Кик"
                                aria-label={`Кик: ${displayName}`}
                                className={w.kickBtn}
                              >
                                <UserMinus className="h-4 w-4" aria-hidden />
                              </button>
                            ) : null}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {waitingAllReady ? (
                <p className={w.footActive}>
                  <Loader2 className={cn('h-3.5 w-3.5 shrink-0 animate-spin', w.spinnerFootActive)} aria-hidden />
                  Старт…
                </p>
              ) : !waitingTableComplete ? (
                <p className={w.footIdle}>
                  <Loader2 className={cn('h-3.5 w-3.5 shrink-0 animate-spin', w.spinnerFootIdle)} aria-hidden />
                  <span className="tabular-nums">
                    {waitingFilled}/{waitingMaxP}
                  </span>
                </p>
              ) : null}
            </div>
          </section>

          <section className={cn(w.panel, 'mt-2 flex min-h-0 flex-col')}>
            <div className={w.panelTopSheen} aria-hidden />
            <p className={cn(w.introTitle, 'relative')}>Чат комнаты</p>
            <p className="relative mt-1 text-[10px] font-semibold uppercase tracking-wide text-stone-500">
              Только игроки за этим столом (ожидание старта)
            </p>
            <div
              className={cn(
                'online-lobby-waiting-chat relative mt-2 max-h-44 min-h-[6.5rem] space-y-1.5 overflow-y-auto rounded-sm border bg-black/45 px-2 py-2',
                w.chatScrollBorder
              )}
              role="log"
              aria-live="polite"
            >
              {!waitingChatMessages.length ? (
                <p className="py-4 text-center text-[11px] text-stone-500">Пока нет сообщений</p>
              ) : (
                waitingChatMessages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      'rounded-sm border px-2 py-1.5 text-[11px] leading-snug',
                      m.self ? w.chatBubbleSelf : 'mr-4 border-white/10 bg-white/[0.04] text-stone-100'
                    )}
                  >
                    <span className={w.chatNameAccent}>{m.username}</span>
                    <span className="mx-1 text-stone-600">·</span>
                    <span className="text-stone-200">{m.text}</span>
                  </div>
                ))
              )}
            </div>
            <div className="relative mt-2 flex gap-2">
              <input
                type="text"
                value={waitingChatDraft}
                onChange={(e) => setWaitingChatDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendWaitingTableChat();
                  }
                }}
                maxLength={280}
                disabled={!connected || !waiting}
                placeholder="Сообщение…"
                className={w.chatInput}
              />
              <button
                type="button"
                onClick={sendWaitingTableChat}
                disabled={!connected || !waitingChatDraft.trim()}
                className={w.chatSendBtn}
              >
                Отпр.
              </button>
            </div>
          </section>
        </main>

        {inviteFriendModalOpen ? (
          <div
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/75 p-3 sm:items-center"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) setInviteFriendModalOpen(false);
            }}
          >
            <div
              className="relative flex max-h-[min(85dvh,540px)] w-full max-w-md min-h-0 flex-col"
              role="dialog"
              aria-modal="true"
              aria-labelledby="online-lobby-invite-title"
              onClick={(e) => e.stopPropagation()}
            >
              <section
                className={cn(
                  w.panel,
                  'flex min-h-0 w-full flex-1 flex-col overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.65)]'
                )}
              >
                <div className={w.panelTopSheen} aria-hidden />
                <div className={cn('relative flex items-start justify-between gap-2 border-b pb-3', w.modalDivider)}>
                  <div className="min-w-0">
                    <p className={w.introTitle}>Комната</p>
                    <h2
                      id="online-lobby-invite-title"
                      className="mt-1 text-sm font-black uppercase tracking-wide text-stone-100 sm:text-base"
                    >
                      Пригласить кореша
                    </h2>
                    <p className={cn('mt-1 font-mono text-xs font-bold tracking-wider', w.monoAccent)}>{roomCode}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setInviteFriendModalOpen(false)}
                    className={cn(w.backBtn, '!h-9 !w-9 shrink-0')}
                    aria-label="Закрыть"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </div>
                <ul className="mt-3 flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
                  {(() => {
                    const { byId, byName } = inviteFriendPresenceMaps;
                    return sortedInviteFriends.map((friend) => {
                      const badge = inviteFriendPresenceBadge(friend, byId, byName);
                      const initial = String(friend.username || 'И').trim().charAt(0).toUpperCase();
                      const lvl = Math.max(1, Math.floor(Number(friend.level) || 1));
                      const av = String(friend.avatarUrl || '').trim();
                      return (
                        <li key={String(friend.id || friend.username)} className={cn(w.playerRow, '!py-2')}>
                          <div className={cn(w.avatarWrap, '!h-10 !w-10 text-xs')}>
                            {av ? (
                              <img src={av} alt="" className="h-full w-full object-cover" draggable={false} />
                            ) : (
                              initial
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                              <p className={cn(w.name, '!text-xs sm:!text-sm')}>{friend.username}</p>
                              <span
                                className={cn(
                                  'shrink-0 rounded-sm border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide',
                                  badge.className
                                )}
                              >
                                {badge.text}
                              </span>
                              <span className="text-[9px] font-bold text-stone-500">ур.{lvl}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => copyRoomInviteForFriend(friend)}
                            className={w.secondaryActionBtn}
                          >
                            Копировать
                          </button>
                        </li>
                      );
                    });
                  })()}
                </ul>
                {!sortedInviteFriends.length ? (
                  <p className={w.emptyPanel}>
                    Принятой братвы пока нет. Отправляйте или принимайте запрос закорефаниться в «Братва» или из профиля
                    в глобальном чате.
                  </p>
                ) : null}
              </section>
            </div>
          </div>
        ) : null}

        {toasts.map((t) => (
          <NotificationToast
            key={t.id}
            message={t.message}
            type={t.type}
            onClose={() => removeToast(t.id)}
          />
        ))}

        <LobbyBottomNav
          mode={mode === 'poker' ? 'poker' : mode === 'belot' ? 'belot' : 'seka'}
          onOpenGifts={onOpenGifts}
          onOpenFriends={onOpenFriends}
          onOpenCreateRoom={openCreateModal}
          giftsNavBadgeCount={giftsNavBadgeCount}
          friendsNavBadgeCount={friendsNavBadgeCount}
        />
      </div>
    );
  }

  return (
    <div className="pt-root relative flex min-h-[100dvh] min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <ArcadeModeBackdrop mode={theme.mode} />
      <header className="relative z-20 border-b border-white/10 bg-gradient-to-b from-[#101a29]/95 to-[#0b1320]/90 px-3 pb-2.5 pt-[max(0.5rem,env(safe-area-inset-top,0px))] shadow-[0_10px_32px_rgba(0,0,0,0.35)] backdrop-blur-md sm:px-4">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={handleBack}
            className="lobby-focus inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-slate-100 transition hover:border-white/30 hover:bg-white/[0.08]"
            aria-label="Назад"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
          <div className="text-center">
            <p className={cn('text-[10px] font-bold uppercase tracking-[0.14em]', theme.accentText)}>
              {theme.modeLabel} · Онлайн
            </p>
            <h1 className="text-sm font-semibold text-slate-100 sm:text-base">Онлайн лобби</h1>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {typeof onOpenMail === 'function' ? (
              <button
                type="button"
                onClick={onOpenMail}
                className="lobby-focus relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.04] text-slate-100 transition hover:border-white/30 hover:bg-white/[0.08]"
                aria-label={
                  mailUnreadCount > 0 ? `Почта, непрочитано: ${mailUnreadCount}` : 'Почта'
                }
              >
                <LobbyHubNotificationIndicator count={mailUnreadCount} layout="absoluteCorner" />
                <Mail className="relative z-[2] h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
              </button>
            ) : null}
            <div
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold',
                connected
                  ? 'border-emerald-400/35 bg-emerald-950/30 text-emerald-300'
                  : 'border-red-400/35 bg-red-950/30 text-red-300'
              )}
            >
              {connected ? <Wifi className="h-3.5 w-3.5" aria-hidden /> : <WifiOff className="h-3.5 w-3.5" aria-hidden />}
              {connected ? 'Online' : 'Offline'}
            </div>
          </div>
        </div>
      </header>

      <main className="safe-scroll-top relative z-10 mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col gap-2 overflow-y-auto overscroll-y-contain px-3 pb-[var(--lobby-bottom-nav-h)] pt-3 [scroll-padding-bottom:var(--lobby-bottom-nav-h)] sm:px-4">
        {error ? (
          <div
            className="mb-1 flex items-start gap-2 rounded-xl border border-red-500/40 bg-red-950/40 px-3 py-2.5 text-sm text-red-200"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" aria-hidden />
            <span>{error}</span>
          </div>
        ) : null}
        {reconnectWaitContext ? (
          <div
            className="mb-1 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-950/40 px-3 py-2.5 text-sm text-amber-100"
            role="status"
          >
            <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-amber-300" aria-hidden />
            <span>
              Матч на паузе: ждём возвращение игрока.
              {reconnectWaitSecLeft != null ? ` Осталось ${reconnectWaitSecLeft} с.` : ''}
            </span>
          </div>
        ) : null}

        <section
          className={cn(
            'rounded-2xl border p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-black/20',
            theme.panelCard
          )}
        >
          {/* Бренд + статус */}
          <div className="flex items-start gap-3">
            <div className="relative shrink-0">
              <div
                className={cn(
                  'pointer-events-none absolute -inset-1 rounded-xl opacity-60 blur-md',
                  modeIconAuraClass
                )}
                aria-hidden
              />
              <img
                src={theme.logoSrc}
                alt=""
                className="relative h-12 w-auto object-contain"
                aria-hidden
                draggable={false}
              />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-sm font-semibold text-slate-100 sm:text-[15px]">{theme.modeLabel} онлайн</h2>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium',
                    connected
                      ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200/90'
                      : 'border-red-400/30 bg-red-500/10 text-red-200/90'
                  )}
                >
                  {connected ? <Wifi className="h-3 w-3" aria-hidden /> : <WifiOff className="h-3 w-3" aria-hidden />}
                  {connected ? 'онлайн' : 'офлайн'}
                </span>
              </div>
              <p className="mt-1 text-[11px] leading-snug text-slate-400">
                Поиск по коду, список столов и создание комнаты — в одном месте.
              </p>
              {isBelotMode ? (
                <div className="mt-2 inline-flex rounded-lg border border-white/10 bg-black/25 p-1">
                  <button
                    type="button"
                    onClick={() => setBelotVariantUi('armenian')}
                    className={cn(
                      'lobby-focus rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition',
                      belotVariantUi === 'armenian'
                        ? 'bg-violet-500/25 text-violet-100'
                        : 'text-slate-400 hover:text-slate-200'
                    )}
                    title="Армянский белот"
                  >
                    <span className="inline-flex items-center gap-1 normal-case">
                      <span
                        aria-hidden
                        style={{ fontFamily: 'Segoe UI Emoji, Apple Color Emoji, Noto Color Emoji, sans-serif' }}
                      >
                        🇦🇲
                      </span>
                      <span>Армянский</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBelotVariantUi('moldovan')}
                    className={cn(
                      'lobby-focus rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition',
                      belotVariantUi === 'moldovan'
                        ? 'bg-violet-500/25 text-violet-100'
                        : 'text-slate-400 hover:text-slate-200'
                    )}
                    title="Молдавский белот"
                  >
                    <span className="inline-flex items-center gap-1 normal-case">
                      <span
                        aria-hidden
                        style={{ fontFamily: 'Segoe UI Emoji, Apple Color Emoji, Noto Color Emoji, sans-serif' }}
                      >
                        🇲🇩
                      </span>
                      <span>Молдавский</span>
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div className="my-4 border-t border-white/10" />

          {/* Панель: обновить → поиск → создать (Tab и визуальный порядок совпадают) */}
          <div className="flex items-stretch gap-2">
            <button
              type="button"
              onClick={() => transport?.emit('GET_ROOM_LIST')}
              disabled={!connected}
              className={cn(
                'lobby-focus inline-flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-lg border transition disabled:pointer-events-none disabled:opacity-40',
                theme.actionBtn
              )}
              title="Обновить список"
              aria-label="Обновить список столов"
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
            </button>
            <label className="flex min-w-0 flex-1 cursor-text items-center gap-2 rounded-lg bg-black/25 px-3 py-2 ring-1 ring-white/5">
              <Search className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
              <input
                type="text"
                placeholder="Код или название комнаты"
                value={roomSearch}
                onChange={(e) => setRoomSearch(e.target.value)}
                className="min-w-0 flex-1 bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-500"
              />
            </label>
            <button
              type="button"
              onClick={openCreateModal}
              disabled={!playerName || !connected}
              className={cn(
                'lobby-focus inline-flex h-10 w-10 shrink-0 items-center justify-center self-center rounded-lg border transition disabled:pointer-events-none disabled:opacity-40',
                theme.actionBtn
              )}
              title="Создать комнату"
              aria-label="Создать комнату"
            >
              <Plus className="h-5 w-5" aria-hidden />
            </button>
          </div>

          <div className="my-4 border-t border-white/10" />

          {/* Фильтры списка + пресет для формы «Создать» */}
          <div className="rounded-lg bg-black/[0.18] ring-1 ring-white/[0.06]">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
              className={cn(
                'lobby-focus flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left transition hover:bg-white/[0.04]',
                filtersOpen && 'rounded-b-none border-b border-white/10'
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <SlidersHorizontal className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                <span className="text-xs font-semibold text-slate-200">Фильтры</span>
                <span className="hidden truncate text-[11px] text-slate-500 sm:inline">
                  по входу и анте; то же в «Создать»
                </span>
              </span>
              <span className="flex min-w-0 shrink-0 items-center gap-2">
                <span
                  className={cn(
                    'max-w-[10rem] truncate rounded-md bg-white/[0.06] px-2 py-0.5 font-mono text-[10px] font-medium tabular-nums sm:max-w-[11rem]',
                    theme.accentText
                  )}
                >
                  {selectedPlayerStake.toLocaleString('ru-RU')}
                  {isSekaMode ? ` / ${selectedAnte.toLocaleString('ru-RU')}` : ''}
                </span>
                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 text-slate-400 transition-transform', filtersOpen && 'rotate-180')}
                  aria-hidden
                />
              </span>
            </button>
            {filtersOpen ? (
              <div className="space-y-4 px-3 pb-3 pt-1">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-xs font-medium text-slate-400">Вход игрока / команда</span>
                    <span className={cn('font-mono text-xs font-semibold tabular-nums', theme.accentText)}>
                      {selectedPlayerStake.toLocaleString('ru-RU')} / игрок · команда{' '}
                      {Number(selectedTeamStake).toLocaleString('ru-RU')} SEKA
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={ROOM_STAKE_OPTIONS.length - 1}
                    step={1}
                    value={createStakeIndex}
                    title="Комиссия платформы: 2%"
                    onChange={(e) => setCreateStakeIndex(Number(e.target.value))}
                    className="h-2.5 w-full cursor-pointer appearance-none rounded-full bg-white/[0.08]"
                    style={{
                      background: `linear-gradient(to right, ${theme.sliderTrack} 0%, ${theme.sliderTrack} ${(createStakeIndex / (ROOM_STAKE_OPTIONS.length - 1)) * 100}%, rgba(255,255,255,0.08) ${(createStakeIndex / (ROOM_STAKE_OPTIONS.length - 1)) * 100}%, rgba(255,255,255,0.06) 100%)`,
                    }}
                  />
                  <p className="text-[10px] text-slate-500">
                    Список столов показывает только комнаты с этим входом. Комиссия 2%.
                  </p>
                </div>

                {isSekaMode ? (
                  <div className="border-t border-white/10 pt-3">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="text-xs font-medium text-slate-400">Анте за раздачу</span>
                        <span className={cn('font-mono text-xs font-semibold tabular-nums', theme.accentText)}>
                          {selectedAnte.toLocaleString('ru-RU')} SEKA
                          <span className="font-sans text-[10px] font-normal text-slate-500">
                            {' '}
                            (≤ взнос {selectedPlayerStake.toLocaleString('ru-RU')})
                          </span>
                        </span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={maxAnteIdxLobby}
                        step={1}
                        value={Math.min(createAnteIndex, maxAnteIdxLobby)}
                        onChange={(e) => setCreateAnteIndex(Number(e.target.value))}
                        title="Базовое анте за руку в секе (до старта множитель свары)"
                        className="h-2.5 w-full cursor-pointer appearance-none rounded-full bg-white/[0.08]"
                        style={{
                          background: `linear-gradient(to right, ${theme.sliderTrack} 0%, ${theme.sliderTrack} ${anteFillPercentLobby}%, rgba(255,255,255,0.08) ${anteFillPercentLobby}%, rgba(255,255,255,0.06) 100%)`,
                        }}
                      />
                      <p className="text-[10px] text-slate-500">
                        Если у стола в лобби указано анте — показываем только с тем же значением; без анте в
                        данных стол не режем по анте. Не больше выбранного взноса.
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="my-4 border-t border-white/10" />

          {/* Столы */}
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <span className="text-sm font-semibold text-slate-200">Столы</span>
              <span className="shrink-0 rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] font-medium tabular-nums text-slate-400 ring-1 ring-white/10">
                {filteredRooms.length}/{normalizedRooms.length}
              </span>
            </div>
            <Swords className={cn('h-4 w-4 shrink-0 opacity-40', theme.iconTone)} aria-hidden />
          </div>

          <div className="overflow-hidden rounded-lg ring-1 ring-white/[0.06]">
            {filteredRooms.length === 0 ? (
              <div className="bg-black/15 px-4 py-8 text-center">
                <p className="text-sm text-slate-400">
                  {normalizedRooms.length === 0 ? 'Пока нет открытых комнат' : 'Ничего не найдено по фильтру'}
                </p>
                <p className="mt-1 text-[11px] text-slate-500">
                  Создайте комнату, измените поиск или откройте «Фильтры» и смените вход / анте
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-white/[0.06]">
                {filteredRooms.map((room) => {
                  const seated = Number(room.seated ?? (room.players?.length || 0));
                  const maxP = Number(room.maxPlayers ?? 2);
                  const displayTitle = String(room.name || '').trim() || room.code;
                  const modeShort = String(theme.modeLabel || '')
                    .toUpperCase()
                    .slice(0, 4);
                  return (
                    <li key={room.code}>
                      <div className="flex items-stretch justify-between gap-2 px-1.5 py-2 transition hover:bg-white/[0.03] sm:px-2">
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <span
                              className={cn(
                                'inline-flex h-[22px] shrink-0 items-center gap-0.5 rounded-full border px-1 py-px',
                                'border-white/25 bg-black/25 text-[6px] font-black uppercase tracking-tight text-white/90'
                              )}
                              title={theme.modeLabel}
                            >
                              <img
                                src={theme.logoSrc}
                                alt=""
                                className="h-3 w-3 object-contain opacity-95"
                                draggable={false}
                              />
                              {modeShort}
                            </span>
                            <h3
                              className="min-w-0 flex-1 truncate text-[10px] font-bold leading-tight text-white"
                              title={displayTitle}
                            >
                              {displayTitle}
                            </h3>
                            <span
                              className={cn(
                                'shrink-0 rounded px-1 py-px text-[6px] font-black uppercase leading-none ring-1',
                                'bg-emerald-500/15 text-emerald-200 ring-emerald-500/30'
                              )}
                            >
                              Ожидание
                            </span>
                          </div>
                          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                            <div className="flex shrink-0 flex-wrap items-center gap-px">
                              {(room.players || []).length ? (
                                room.players.map((pid, idx) => (
                                  <OnlineLobbyCompactSeatChip
                                    key={`${room.code}-p-${String(pid)}-${idx}`}
                                    userId={pid}
                                    room={room}
                                    user={user}
                                  />
                                ))
                              ) : (
                                <span className="text-[8px] font-medium text-slate-600">—</span>
                              )}
                            </div>
                            <span className="flex min-w-0 items-center gap-0.5 truncate text-[8px] font-semibold tabular-nums text-slate-500">
                              <span className="shrink-0 text-slate-500">{seated}/{maxP}</span>
                              <span className="text-slate-600" aria-hidden>
                                ·
                              </span>
                              <span className="flex shrink-0 items-center gap-0.5 font-mono text-[#c9a227]/95">
                                {room.currency === 'bucks' ? (
                                  <img
                                    src={BUCKS_IMG}
                                    alt=""
                                    className="h-2 w-2 shrink-0 object-contain opacity-90"
                                    draggable={false}
                                  />
                                ) : (
                                  <LobbyFiskiIcon className="h-2 w-2" size="sm" />
                                )}
                                {room.currency === 'bucks'
                                  ? Number(room.stake).toLocaleString('ru-RU', {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                  : Number(room.stake).toLocaleString('ru-RU')}
                                <span className="font-sans text-[7px] font-bold uppercase text-slate-500">
                                  {room.currency === 'bucks' ? '€' : ''}
                                </span>
                              </span>
                              {mode === 'seka' && Number(room.ante) > 0 ? (
                                <>
                                  <span className="text-slate-600" aria-hidden>
                                    ·
                                  </span>
                                  <span className="shrink-0">
                                    анте {Number(room.ante).toLocaleString('ru-RU')}
                                  </span>
                                </>
                              ) : null}
                              {mode === 'belot' && room.belotVariant ? (
                                <>
                                  <span className="text-slate-600" aria-hidden>
                                    ·
                                  </span>
                                  <span className="shrink-0">
                                    {room.belotVariant === 'moldovan'
                                      ? 'молдавский'
                                      : room.belotVariant === 'open_duell'
                                        ? 'молдавский 1×1'
                                        : 'армянский'}
                                  </span>
                                  {room.belotTargetScore ? (
                                    <>
                                      <span className="text-slate-600" aria-hidden>
                                        ·
                                      </span>
                                      <span className="shrink-0">
                                        до {belotMatchTargetCpFromEngine(room.belotTargetScore)} CP
                                      </span>
                                    </>
                                  ) : null}
                                  {room.belotShulerEnabled ? (
                                    <>
                                      <span className="text-slate-600" aria-hidden>
                                        ·
                                      </span>
                                      <span
                                        className="shrink-0 rounded border border-amber-500/40 bg-amber-950/30 px-0.5 font-mono text-[8px] font-bold text-amber-200/95"
                                        title="Режим Шулера"
                                      >
                                        SHU
                                      </span>
                                    </>
                                  ) : null}
                                </>
                              ) : null}
                            </span>
                          </div>
                          <p className="flex items-center gap-0.5 font-mono text-[8px] font-semibold tracking-[0.08em] text-slate-500">
                            {room.isPrivate ? (
                              <Lock className={cn('h-2.5 w-2.5 shrink-0 opacity-90', theme.accentText)} aria-label="Приватная" />
                            ) : null}
                            {room.code}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => requestJoinRoom(room)}
                          disabled={!playerName || !connected}
                          className={cn(
                            'lobby-focus mt-0.5 inline-flex h-[26px] shrink-0 items-center gap-0.5 self-start rounded-md border px-1.5 text-[9px] font-bold transition disabled:pointer-events-none disabled:opacity-40',
                            theme.joinBtn
                          )}
                        >
                          <DoorOpen className="h-2.5 w-2.5" aria-hidden />
                          Войти
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        <section
          className={cn(
            'rounded-2xl border p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] ring-1 ring-black/20',
            theme.panelCard
          )}
        >
          <div className="flex items-start gap-2.5">
            <MessagesSquare className={cn('mt-0.5 h-4 w-4 shrink-0', theme.accentText)} aria-hidden />
            <div className="min-w-0">
              <h2 className="text-xs font-bold uppercase tracking-wide text-slate-200">Чат комнаты</h2>
              <p className="mt-1 text-[10px] leading-relaxed text-slate-500">
                Приватный чат только для игроков за вашим столом в ожидании старта. Создайте комнату или войдите по коду —
                на экране ожидания откроется чат вместе со списком игроков.
              </p>
            </div>
          </div>
        </section>
      </main>

      <OnlineStyleCreateRoomModal
        open={createModalOpen}
        onClose={() => !createSubmitting && setCreateModalOpen(false)}
        mode={mode}
        user={user}
        connected={connected}
        submitting={createSubmitting}
        externalError={error}
        onClearExternalError={() => setError('')}
        preset={createModalPreset}
        onPresetApplied={() => setCreateModalPreset(null)}
        onConfirm={(payload) => {
          if (!playerName) {
            setError('Не найден никнейм пользователя. Войдите в аккаунт заново.');
            return;
          }
          if (!connected) {
            setError('Нет соединения с лобби');
            return;
          }
          setError('');
          setCreateSubmitting(true);
          transport?.emit('CREATE_ROOM', {
            playerName,
            mode,
            buyIn: payload.buyIn,
            currency: payload.currency || 'seka',
            maxPlayers: payload.maxPlayers,
            name: payload.name,
            ...(mode === 'seka' && payload.ante != null ? { ante: payload.ante } : {}),
            ...(mode === 'belot'
              ? {
                  belotVariant: payload.belotVariant || 'armenian',
                  belotTargetScore:
                    Number(payload.belotTargetScore) === 5010 || Number(payload.belotTargetScore) === 501
                      ? 5010
                      : 10010,
                  belotShulerEnabled: Boolean(
                    payload.belotShulerEnabled &&
                      Number(payload.maxPlayers) === 4 &&
                      String(payload.belotVariant || 'armenian') !== 'open_duell',
                  ),
                }
              : {}),
            ...(payload.isPrivate ? { isPrivate: true, roomPassword: payload.roomPassword } : {}),
          });
        }}
      />

      {joinPasswordModal ? (
        <div
          className="fixed inset-0 z-[101] flex items-end justify-center bg-black/60 px-2 py-3 backdrop-blur-sm sm:items-center sm:p-4"
          role="presentation"
          onClick={() => setJoinPasswordModal(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="online-join-password-title"
            className={cn(
              'relative w-full max-w-sm rounded-2xl border border-white/10 bg-gradient-to-b p-4 shadow-2xl ring-1 outline-none',
              roomTheme.shell,
              roomTheme.ring
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-start justify-between gap-2">
              <h2 id="online-join-password-title" className="text-sm font-black uppercase tracking-[0.12em] text-white/95">
                Пароль комнаты
              </h2>
              <button
                type="button"
                onClick={() => setJoinPasswordModal(null)}
                className="lobby-focus inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/15 bg-white/5 text-[#b8c9e0] transition hover:bg-white/10 hover:text-white"
                aria-label="Закрыть"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <p className="mb-2 font-mono text-lg font-bold tracking-[0.15em] text-white/90">{joinPasswordModal.code}</p>
            <label className="mb-1.5 block text-xs font-medium text-[#8fa3bf]" htmlFor="online-join-room-password">
              Пароль
            </label>
            <input
              id="online-join-room-password"
              type="password"
              autoComplete="off"
              value={joinPasswordDraft}
              onChange={(e) => setJoinPasswordDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleJoinRoom(joinPasswordModal.code, joinPasswordDraft);
                  setJoinPasswordModal(null);
                }
              }}
              className={cn(
                'lobby-focus mb-4 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-1',
                roomTheme.inputFocus
              )}
              placeholder="Введите пароль"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setJoinPasswordModal(null)}
                className="lobby-focus flex-1 rounded-xl border border-white/15 py-2.5 text-sm font-semibold text-[#b8c9e0] transition duration-150 hover:bg-white/5"
              >
                Отмена
              </button>
              <button
                type="button"
                onClick={() => {
                  handleJoinRoom(joinPasswordModal.code, joinPasswordDraft);
                  setJoinPasswordModal(null);
                }}
                disabled={!playerName || !connected || joinPasswordDraft.trim().length < 4}
                className={cn(
                  'lobby-focus flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-b py-2.5 text-sm font-black shadow-md transition duration-150 ease-out disabled:cursor-not-allowed disabled:opacity-45',
                  roomTheme.primaryBtn
                )}
              >
                Войти
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <LobbyBottomNav
        mode={mode === 'poker' ? 'poker' : mode === 'belot' ? 'belot' : 'seka'}
        onOpenGifts={onOpenGifts}
        onOpenFriends={onOpenFriends}
        onOpenCreateRoom={openCreateModal}
        giftsNavBadgeCount={giftsNavBadgeCount}
        friendsNavBadgeCount={friendsNavBadgeCount}
      />

      {toasts.map((t) => (
        <NotificationToast
          key={t.id}
          message={t.message}
          type={t.type}
          onClose={() => removeToast(t.id)}
        />
      ))}
    </div>
  );
};

export default OnlineLobby;